import { useState, useEffect, useCallback, useRef } from "react";
import Map, { Marker, NavigationControl } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import logoHorizontal from './Assets/OLC-simple.webp';
import logoBadge from './Assets/OLC-badge.webp';
import logoPrimary from './Assets/OLC-primary.webp';
import logoOLC from './Assets/OLC_Outdoors_logo.webp';
import logoPrimaryClear from './Assets/OLC-Primary-Clear.webp';
import { supabase } from "./supabase";
import Auth from "./Auth";
import FishWhistler from "./FishWhistler";

// ─── CHECKOUT HELPER ─────────────────────────────────────────────────────────
async function startCheckout(tier, setCheckoutLoading, setCheckoutError) {
  setCheckoutLoading(tier);
  setCheckoutError("");
  try {
    // Force refresh the session to get a fresh token
    const refreshResult = await supabase.auth.refreshSession();
    const session = refreshResult.data.session;
    if (!session?.access_token) {
      setCheckoutError("Please sign in to upgrade.");
      setCheckoutLoading(null);
      return;
    }
    const res = await fetch(
      "https://lpdmfvvnqvubdataqaag.supabase.co/functions/v1/create-checkout-session",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ tier }),
      }
    );
    const data = await res.json();
    if (data.url) {
      window.location.href = data.url;
    } else {
      setCheckoutError("Something went wrong. Please try again.");
    }
  } catch {
    setCheckoutError("Connection error. Please try again.");
  } finally {
    setCheckoutLoading(null);
  }
}

// ─── PRICING PAGE ─────────────────────────────────────────────────────────────
function PricingPage({ user, nav, s }) {
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");

  const plans = [
    {
      tier: "free",
      name: "Free",
      price: "$0",
      period: "forever",
      color: C.slateLight,
      features: [
        "3 Fish Whistler questions/day",
        "Daily fishing briefing (always free)",
        "Full fishing intelligence app",
        "Trip reports & community",
      ],
      cta: "Current Plan",
      disabled: true,
    },
    {
      tier: "angler",
      name: "Angler",
      price: "$6",
      period: "per month",
      color: C.orange,
      badge: "MOST POPULAR",
      features: [
        "20 Fish Whistler questions/day",
        "Daily fishing briefing (always free)",
        "Detailed tactics, gear & hatch info",
        "Full fishing intelligence app",
        "Trip reports & community",
      ],
      cta: "Upgrade to Angler",
      disabled: false,
    },
    {
      tier: "guide",
      name: "Guide",
      price: "$15",
      period: "per month",
      color: C.sand,
      badge: "BEST VALUE",
      features: [
        "Unlimited Fish Whistler questions",
        "Daily fishing briefing (always free)",
        "Expert deep-dives & pro tips",
        "Specific gear recs with sizes & colors",
        "Access notes & regulation details",
        "Full fishing intelligence app",
        "Trip reports & community",
      ],
      cta: "Upgrade to Guide",
      disabled: false,
    },
  ];

  return (
    <div>
      <div style={{ background: `linear-gradient(160deg, #080f18, ${C.navyMid})`, padding: "80px 24px 60px", textAlign: "center", borderBottom: `1px solid ${C.border}` }}>
        <div style={s.heroTag}>Pricing</div>
        <h1 style={{ ...s.h1, fontSize: "clamp(30px, 5vw, 54px)" }}>
          Fish <span style={s.h1accent}>Smarter</span>
        </h1>
        <p style={s.tagline}>Upgrade your time on the water.</p>
      </div>

      <div style={{ ...s.section, paddingTop: 60 }}>
        {checkoutError && (
          <div style={{ ...s.error, textAlign: "center", marginBottom: 24, fontSize: 15 }}>
            {checkoutError}
          </div>
        )}

        {!user && (
          <div style={{ textAlign: "center", marginBottom: 36, background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24 }}>
            <div style={{ color: C.sand, fontSize: 15, marginBottom: 12 }}>Sign in to upgrade your account</div>
            <button style={s.btnPrimary} onClick={() => nav("Account")}>Sign In / Create Account</button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, maxWidth: 900, margin: "0 auto" }}>
          {plans.map((plan) => (
            <div key={plan.tier} style={{
              background: C.cardBg,
              border: `2px solid ${plan.tier === "angler" ? C.orange : C.border}`,
              borderRadius: 8,
              padding: 32,
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}>
              {plan.badge && (
                <div style={{ position: "absolute", top: -13, left: "50%", transform: "translateX(-50%)", background: plan.tier === "angler" ? C.orange : C.sand, color: "#fff", fontSize: 10, fontWeight: 700, padding: "3px 14px", borderRadius: 2, letterSpacing: 2, whiteSpace: "nowrap" }}>
                  {plan.badge}
                </div>
              )}
              <div style={{ fontSize: 11, color: plan.color, letterSpacing: 3, fontWeight: 700, marginBottom: 8, textTransform: "uppercase" }}>{plan.name}</div>
              <div style={{ fontSize: 42, fontWeight: 900, color: C.white, lineHeight: 1 }}>{plan.price}</div>
              <div style={{ fontSize: 13, color: C.slateLight, marginBottom: 28 }}>{plan.period}</div>
              <div style={{ flex: 1, marginBottom: 28 }}>
                {plan.features.map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
                    <span style={{ color: plan.color, fontSize: 14, marginTop: 1 }}>✓</span>
                    <span style={{ color: C.slateLight, fontSize: 14, lineHeight: 1.5 }}>{f}</span>
                  </div>
                ))}
              </div>
              <button
                disabled={plan.disabled || checkoutLoading === plan.tier || !user}
                onClick={() => !plan.disabled && user && startCheckout(plan.tier, setCheckoutLoading, setCheckoutError)}
                style={{
                  width: "100%",
                  padding: "13px 0",
                  background: plan.disabled ? C.navyMid : plan.tier === "angler" ? C.orange : `${C.sand}22`,
                  border: `1px solid ${plan.disabled ? C.border : plan.tier === "angler" ? C.orange : C.sand}`,
                  color: plan.disabled ? C.textMuted : plan.tier === "angler" ? "#fff" : C.sand,
                  borderRadius: 4,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: plan.disabled || !user ? "default" : "pointer",
                  letterSpacing: 1,
                  opacity: checkoutLoading && checkoutLoading !== plan.tier ? 0.5 : 1,
                }}
              >
                {checkoutLoading === plan.tier ? "Redirecting…" : plan.cta}
              </button>
            </div>
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 48, color: C.slateLight, fontSize: 13 }}>
          <p>All plans include a free daily fishing briefing. Cancel anytime.</p>
          <p style={{ marginTop: 8 }}>Questions? Email us at <span style={{ color: C.sand }}>hello@onelastcast.org</span></p>
        </div>
      </div>
    </div>
  );
}

// ─── CONFIRMATION PAGE ────────────────────────────────────────────────────────
function ConfirmationPage({ nav }) {
  return (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "60px 24px" }}>
      <div style={{ textAlign: "center", maxWidth: 480 }}>
        <div style={{ fontSize: 64, marginBottom: 24 }}>🎣</div>
        <h1 style={{ fontSize: 32, color: C.white, fontWeight: 900, marginBottom: 12 }}>
          You're <span style={{ color: C.orange }}>upgraded!</span>
        </h1>
        <p style={{ color: C.slateLight, fontSize: 16, lineHeight: 1.7, marginBottom: 12 }}>
          Welcome to the next level. Your account has been upgraded and Fish Whistler is ready to go deep.
        </p>
        <p style={{ color: C.textMuted, fontSize: 13, marginBottom: 36, fontStyle: "italic" }}>
          It may take a moment for your new tier to appear — refresh if needed.
        </p>
        <button style={{ background: "#c45e2a", color: "#fff", border: "none", padding: "14px 40px", fontSize: 15, fontWeight: "bold", cursor: "pointer", letterSpacing: 2, borderRadius: 2, textTransform: "uppercase" }} onClick={() => nav("Whistler")}>
          Open Fish Whistler →
        </button>
      </div>
    </div>
  );
}

// ─── COLORS ───────────────────────────────────────────────────────────────────
const C = {
  navy:"#0d1b2a", navyLight:"#1a2e45", navyMid:"#152338",
  orange:"#c45e2a", orangeLight:"#d97040",
  sand:"#d4b896", sandLight:"#e8d5be",
  slate:"#4a5568", slateLight:"#718096",
  white:"#f5f0e8", cardBg:"#111f30", border:"#1e3a55",
  bgDeep:"#0d1b2a", bgCard:"#111f30", bgInner:"#152338",
  textPrimary:"#f5f0e8", textSecondary:"#d4b896", textMuted:"#718096",
  scoreLow:"#4a5568",
};

const STATE_COLORS={VA:"#c45e2a",NC:"#22d3ee",TN:"#f59e0b",MD:"#a78bfa",GA:"#34d399",PA:"#fb7185",NY:"#38bdf8",ME:"#f0abfc",VT:"#86efac",NH:"#fde68a",WV:"#fdba74",KY:"#67e8f9",CO:"#c4b5fd",TX:"#fca5a5",NM:"#6ee7b7",AZ:"#fcd34d",UT:"#a5b4fc",ID:"#f9a8d4",MT:"#d9f99d",WY:"#bfdbfe",WA:"#99f6e4",OR:"#fecdd3",CA:"#fed7aa"};

// ─── FISHING APP DATA ─────────────────────────────────────────────────────────
const RAW=[
[1,"Mossy Creek","VA","Shenandoah Valley",38.358,-79.029,"01632900",["fly"],["Brown Trout","Brook Trout"],"hard","low",15,60,"Legendary limestone spring creek. Selective fish, crystal clear water, technical dry fly. One of the finest wild trout fisheries in the East.","C&R Artificial Only","BWOs spring/fall. Use 6x–7x tippet. Approach low.",38.3605,-79.0318,"Mossy Creek public access","Mossy Creek Fly Fishing","480 E Market St, Harrisonburg, VA",4.9],
[2,"Smith River","VA","Southside VA",36.756,-79.998,"02074000",["fly","spin"],["Brown Trout","Rainbow Trout"],"easy","moderate",120,400,"Virginia's only true tailwater fishery. Cold Philpott Dam releases keep water fishable year-round. Large brown trout in deeper pools.","Special Regs — check VDWR","Midge hatches year-round. Big browns on streamers in fall.",36.759,-79.984,"Smith River — Bassett access","Angler's Choice Marine","Martinsville, VA",4.3],
[3,"Rapidan River","VA","Shenandoah NP",38.343,-78.003,"01665500",["fly"],["Brook Trout","Brown Trout"],"hard","low",40,180,"Wild brook trout in Shenandoah NP. Hike-in only above Hoover Camp. Historic Presidential retreat water.","SNP — Artificial Barbless above Hoover","Small stimulators #14-16. Very slippery rocks — felt soles.",38.437,-78.367,"Rapidan River trailhead","Albemarle Angler","Charlottesville, VA",4.8],
[4,"Passage Creek","VA","Fort Valley",38.825,-78.429,"01634500",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout"],"easy","moderate",20,100,"Beautiful freestone in Fort Valley. Rhododendron-lined banks in GWNF. Stocked and wild trout mix.","Standard Trout Stocking Waters","Inline spinners effective. Early morning near Elizabeth Furnace.",38.825,-78.429,"Passage Creek — Elizabeth Furnace","Front Royal Outdoors","Front Royal, VA",4.8],
[5,"Jackson River","VA","Alleghany Highlands",37.9,-79.85,"02016000",["fly","spin"],["Brown Trout","Rainbow Trout"],"moderate","low",80,350,"Tailwater below Gathright Dam. Trophy brown trout fishery with consistent cold flows. One of Virginia's best kept secrets.","Special Regs below dam — check VDWR","Large streamers for big browns. Sulphur hatches in May.",37.916,-79.858,"Jackson River — Gathright Dam","Mossy Creek Fly Fishing","Harrisonburg, VA",4.9],
[6,"New River (VA)","VA","New River Valley",37.1,-80.7,"03171000",["fly","spin"],["Smallmouth Bass","Musky","Walleye"],"easy","moderate",800,4000,"Ancient river with world-class smallmouth bass. Float fishing ideal through New River Trail State Park.","Standard VA regs","Topwater poppers at dawn. Crawfish patterns mid-summer.",37.102,-80.713,"New River — Claytor Lake access","New River Outdoors","Radford, VA",4.5],
[7,"Clinch River (VA)","VA","Southwest VA",36.9,-82.1,"03486000",["fly","spin"],["Brown Trout","Rainbow Trout","Brook Trout"],"moderate","low",100,500,"Remote SW Virginia gem with excellent wild trout habitat and minimal pressure.","Delayed Harvest sections apply — check VDWR","Sulphurs and caddis. Fish the riffles early morning.",36.905,-82.095,"Clinch River — Cleveland access","Virginia Highlands Outfitters","Abingdon, VA",4.6],
[8,"South Fork Shenandoah","VA","Shenandoah Valley",38.65,-78.5,"01631000",["fly","spin"],["Smallmouth Bass","Largemouth Bass","Catfish"],"easy","moderate",400,2500,"Classic Shenandoah float river. Excellent smallmouth bass habitat.","Standard VA regs","Float June–Sept. Tube jigs and crayfish patterns.",38.651,-78.501,"S. Fork Shenandoah — Luray","Front Royal Outdoors","Front Royal, VA",4.8],
[9,"Rose River","VA","Blue Ridge / SNP",38.482,-78.321,"01664000",["fly"],["Brown Trout","Rainbow Trout","Brook Trout"],"moderate","low",20,90,"Stunning Shenandoah NP stream with cascades and wild trout.","SNP regulations — artificial barbless, C&R","Hike from Dark Hollow Falls trailhead. Attractor patterns work.",38.533,-78.430,"Rose River — Skyline Drive","Albemarle Angler","Charlottesville, VA",4.8],
[10,"South River (VA)","VA","Shenandoah Valley",38.068,-78.889,"02024000",["fly","spin"],["Brown Trout","Rainbow Trout"],"easy","high",50,250,"Urban trout fishery through Waynesboro. Highly accessible via South River Greenway.","Standard stocked trout regs","Fish early before crowds. Evening caddis in spring.",38.068,-78.888,"South River Greenway, Waynesboro","South River Fly Shop","Waynesboro, VA",4.9],
[11,"North Fork Moorman's River","VA","Blue Ridge / SNP",38.13,-78.68,"01662800",["fly"],["Brook Trout","Brown Trout"],"hard","low",10,60,"Wild brook trout in Shenandoah NP. Small, tight stream requiring precise casts.","SNP — Artificial barbless C&R","Stealth approach essential. Small parachute adams #16-18.",38.133,-78.681,"Moorman's River — Sugar Hollow Reservoir","Albemarle Angler","Charlottesville, VA",4.7],
[12,"Pedlar River","VA","Blue Ridge / GWNF",37.68,-79.37,"02027500",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout"],"moderate","low",20,110,"Scenic Jefferson National Forest stream with excellent wild trout.","Standard VDWR stocking regs — GWNF rules apply","Cold headwater feel. Best in spring and fall.",37.682,-79.372,"Pedlar River — Coffey's Mill access","Blue Ridge Angler","Roanoke, VA",4.5],
[13,"Roanoke River (VA)","VA","Southwest VA",37.26,-80.01,"02055000",["fly","spin"],["Brown Trout","Rainbow Trout","Smallmouth Bass"],"easy","high",80,500,"Stocked trout river through Roanoke metro area.","Standard VDWR stocking regs","Hit it early spring when stockings are fresh.",37.261,-80.011,"Roanoke River — Wasena Park","Blue Ridge Angler","Roanoke, VA",4.2],
[14,"Maury River","VA","Bath / Rockbridge Co.",37.8,-79.43,"02024752",["fly","spin"],["Brown Trout","Rainbow Trout","Smallmouth Bass"],"easy","moderate",100,600,"Scenic river through Goshen Pass — one of Virginia's most beautiful gorges.","Standard VDWR regs — Goshen Pass Scenic Area rules","Goshen Pass gorge is breathtaking. Dry flies in summer evenings.",37.801,-79.431,"Maury River — Goshen Pass","Lexington Fly Fishing","Lexington, VA",4.7],
[15,"Potts Creek","VA","Alleghany Highlands",37.78,-80.18,"02017500",["fly","spin"],["Brown Trout","Brook Trout","Rainbow Trout"],"easy","low",15,80,"Small limestone-influenced tributary of Jackson River.","Standard VDWR stocking regs","Wild browns in upper meadow sections. Evening BWOs.",37.781,-80.181,"Potts Creek — Covington access","Mossy Creek Fly Fishing","Harrisonburg, VA",4.4],
[16,"Staunton River","VA","Southside VA",36.73,-79.17,"02075045",["spin"],["Striped Bass","Largemouth Bass","Catfish","Crappie"],"easy","low",500,4000,"Excellent warm-water fishery. Striped bass run in spring.","Standard VA freshwater regs","Spring striper run March–May below Leesville Dam.",36.731,-79.171,"Staunton River — Altavista access","Smith Mountain Lake Outdoors","Hardy, VA",4.3],
[17,"Pound River","VA","Wise Co. / SW VA",37.12,-82.62,"03488400",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout"],"moderate","low",20,100,"Remote southwest Virginia trout stream near Breaks Interstate Park.","Standard VDWR / Delayed Harvest in some sections","Pristine SW Virginia watershed. Caddis-heavy.",37.121,-82.621,"Pound River — Pound access","Virginia Highlands Outfitters","Abingdon, VA",4.5],
[18,"Big Stony Creek","VA","Giles Co.",37.38,-80.58,"03168500",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout"],"easy","moderate",25,120,"Classic GWNF freestone stream in Giles County near Mountain Lake.","Standard VDWR stocking regs","Great family destination. Stimulators and spinners.",37.381,-80.581,"Big Stony Creek — Newport access","New River Outdoors","Radford, VA",4.4],
[19,"Big Wilson Creek","VA","Grayson Co.",36.65,-81.48,"03486898",["fly"],["Brook Trout","Brown Trout"],"moderate","low",10,55,"Outstanding native brook trout stream in Mount Rogers NRA.","C&R Artificial Only in Heritage Waters","Small #14-16 attractors. Stunning high-country scenery.",36.651,-81.481,"Big Wilson Creek — Grayson Highlands SP","Virginia Highlands Outfitters","Abingdon, VA",4.8],
[20,"Holston River (North Fork)","VA","Washington Co.",36.63,-82.05,"03486000",["fly","spin"],["Brown Trout","Rainbow Trout"],"easy","moderate",60,400,"Tailwater character with limestone spring influence.","Standard VDWR / Special regs in some sections","Sulphurs in May-June. Good year-round fishing.",36.631,-82.051,"N. Fork Holston — Saltville access","Virginia Highlands Outfitters","Abingdon, VA",4.5],
[21,"North River (GWNF)","VA","Augusta Co.",38.38,-79.25,"01632000",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout"],"moderate","low",20,100,"Beautiful Shenandoah Mountain stream in GWNF.","Standard VDWR / GWNF rules","North River Gorge area is spectacular. Wading required.",38.381,-79.251,"North River — Todd Lake area","South River Fly Shop","Waynesboro, VA",4.6],
[22,"Dry River","VA","Rockingham Co.",38.47,-79.08,"01630000",["fly"],["Brook Trout","Brown Trout"],"hard","low",8,45,"High-gradient GWNF stream in Rockingham County. Wild brook trout water.","C&R in Heritage Waters section — check VDWR","4WD access road. Small stimulators and elk hair caddis.",38.471,-79.081,"Dry River — Brandywine area","Mossy Creek Fly Fishing","Harrisonburg, VA",4.5],
[23,"Hazel River","VA","Rappahannock Co.",38.48,-78.08,"01664500",["fly","spin"],["Brook Trout","Brown Trout","Smallmouth Bass"],"moderate","moderate",15,80,"SNP boundary stream with good public access below the park.","SNP rules above boundary / Standard VDWR below","Access via Hazel Mountain Trail. Good late-season fishing.",38.481,-78.081,"Hazel River — Hazel Mountain Overlook","Albemarle Angler","Charlottesville, VA",4.4],
[24,"Conway River","VA","Madison Co.",38.34,-78.47,"01663500",["fly"],["Brook Trout","Brown Trout"],"hard","low",10,50,"Remote SNP tributary with excellent wild brook trout.","SNP — Artificial barbless C&R throughout","Long hike required. 100% wild fish. Worth every mile.",38.341,-78.471,"Conway River — Skyline Drive access","Albemarle Angler","Charlottesville, VA",4.7],
[25,"Meadow Run","VA","Augusta Co.",38.21,-78.92,"02024500",["fly"],["Brown Trout","Brook Trout"],"moderate","low",8,40,"Small spring-influenced tributary in Augusta County.","Standard VDWR — wild trout section check regs","Small flies essential. Fish slow and low. 6x minimum.",38.211,-78.921,"Meadow Run — Stuarts Draft","South River Fly Shop","Waynesboro, VA",4.3],
[26,"Tye River","VA","Nelson Co.",37.78,-79.05,"02028500",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout","Smallmouth Bass"],"moderate","moderate",30,200,"Nelson County freestone with diverse fishery.","Standard VDWR regs / GWNF rules in national forest","Good public access via GWNF. Variable flows after rain.",37.781,-79.051,"Tye River — Lovingston access","Blue Ridge Angler","Roanoke, VA",4.4],
[27,"Cheat River (VA headwaters)","VA","Highland Co.",38.55,-79.55,"01600500",["fly"],["Brook Trout","Brown Trout"],"hard","low",10,60,"Highland County headwaters of the Cheat system. Remote wild brook trout.","Standard VDWR — wild trout C&R in upper sections","Bring a topo map. Truly remote. All wild fish.",38.551,-79.551,"Cheat headwaters — Blue Grass area","Mossy Creek Fly Fishing","Harrisonburg, VA",4.6],
[28,"Goose Creek (Loudoun Co.)","VA","Loudoun Co.",39.08,-77.72,"01645000",["fly","spin"],["Smallmouth Bass","Largemouth Bass","Sunfish"],"easy","high",50,400,"Excellent smallmouth bass fishery in Northern Virginia's Loudoun County.","Standard VA freshwater regs","Topwater poppers in summer evenings. Crayfish patterns.",39.081,-77.721,"Goose Creek — Lucketts access","Murray's Fly Shop","Edinburgh, VA",4.3],
[29,"Rappahannock River","VA","Fauquier / Rappahannock Co.",38.48,-77.98,"01667500",["fly","spin"],["Smallmouth Bass","Largemouth Bass","Striped Bass"],"easy","high",200,2000,"Northern Virginia's premier warm-water river. World-class smallmouth bass fishing.","Standard VA freshwater regs","Float from Remington to Fredericksburg. Topwater in summer.",38.481,-77.981,"Rappahannock River — Remington access","Murray's Fly Shop","Edinburgh, VA",4.6],
[30,"Murray's Run / Little Passage Creek","VA","Shenandoah Co.",38.85,-78.45,"01635500",["fly","spin"],["Brook Trout","Brown Trout","Rainbow Trout"],"easy","low",10,60,"Small Shenandoah County freestone stream. Excellent early season stocking.","Standard VDWR stocking regs","Good beginner water. Nearby Shenandoah NP options.",38.851,-78.451,"Passage Creek area — Woodstock","Murray's Fly Shop","Edinburgh, VA",4.4],
[31,"Shenandoah River (N. Fork)","VA","Shenandoah Valley",38.88,-78.55,"01634000",["fly","spin"],["Smallmouth Bass","Rock Bass","Catfish","Largemouth Bass"],"easy","moderate",500,3500,"North Fork of the Shenandoah — excellent smallmouth float water.","Standard VA freshwater regs","Float the scenic valley section. Summer smallmouth on topwater.",38.881,-78.551,"N. Fork Shenandoah — Edinburg","Murray's Fly Shop","Edinburgh, VA",4.7],
[101,"Savage River","MD","Garrett Co.",39.57,-79.12,"01598500",["fly"],["Brown Trout","Rainbow Trout"],"moderate","low",30,120,"MD's premier wild trout stream. Tailwater below Savage River Reservoir.","C&R Artificial Only in trophy section","Small BWOs and midges.",39.572,-79.121,"Savage River — Big Run access","Savage River Lodge","Frostburg, MD",4.8],
[102,"N. Branch Potomac","MD","Allegany Co.",39.7,-79.0,"01596500",["fly","spin"],["Brown Trout","Rainbow Trout"],"easy","moderate",100,500,"Quality tailwater below Jennings Randolph Dam.","Special trout management area regs","Midges and BWOs year-round.",39.703,-79.002,"North Branch — Barnum access","Rocky Gap Outfitters","Cumberland, MD",4.5],
[103,"Gunpowder Falls","MD","Baltimore Co.",39.52,-76.62,"01581500",["fly"],["Brown Trout","Rainbow Trout"],"easy","high",50,300,"Premier urban tailwater near Baltimore. Excellent year-round.","C&R in special management section","Sulphurs in May, Tricos in summer.",39.521,-76.621,"Gunpowder Falls — Masemore Rd","Backwater Angler","Monkton, MD",4.9],
[104,"Big Hunting Creek","MD","Frederick Co.",39.62,-77.46,"01637500",["fly"],["Brown Trout","Brook Trout"],"moderate","moderate",15,80,"Catoctin Mountain Park stream. Historic C&R fishery.","Artificial C&R — oldest such regulation in MD","Small dries and nymphs.",39.622,-77.461,"Cunningham Falls State Park","Catoctin Fly Shop","Thurmont, MD",4.6],
[105,"Potomac River (MD)","MD","Washington Metro",39.0,-77.5,"01646500",["fly","spin"],["Smallmouth Bass","Striped Bass","Catfish"],"easy","high",2000,20000,"Legendary C&O Canal stretch. World-class smallmouth bass fishery.","Standard MD / C&O NHP regs","Float from Point of Rocks. Poppers and Clousers.",39.001,-77.501,"Potomac — C&O Canal","River & Trail Outfitters","Knoxville, MD",4.7],
[201,"Nantahala River","NC","Nantahala NF",35.35,-83.65,"03500000",["fly","spin"],["Rainbow Trout","Brown Trout"],"easy","moderate",150,600,"Famous tailwater in the Nantahala Gorge. Cold and productive year-round.","Hatchery Supported — artificial only in gorge","Sulphurs and caddis #14-18.",35.354,-83.648,"Nantahala — Wesser access","Nantahala Fly Fishing Co.","Bryson City, NC",4.8],
[202,"Davidson River","NC","Pisgah NF",35.283,-82.733,"03441000",["fly"],["Rainbow Trout","Brown Trout","Brook Trout"],"easy","high",50,250,"One of NC's premier trout streams through Pisgah NF.","C&R Artificial Only above Avery Creek","Sulphurs in May, Tricos in summer.",35.283,-82.733,"Davidson River — Pisgah Center","Davidson River Outfitters","Brevard, NC",4.9],
[203,"New River (NC)","NC","High Country",36.45,-81.5,"03167000",["fly","spin"],["Smallmouth Bass","Rainbow Trout"],"easy","low",200,1200,"One of the world's oldest rivers. Excellent smallmouth.","Delayed Harvest sections above Todd","Wading or float trips. Crayfish patterns.",36.451,-81.499,"New River — Jefferson Landing","New River Outfitters","Jefferson, NC",4.7],
[204,"French Broad River","NC","Asheville",35.6,-82.55,"03447687",["fly","spin"],["Smallmouth Bass","Musky","Brown Trout"],"easy","high",600,4000,"Float fishing through Asheville. Excellent smallmouth and musky.","Standard NC regs","Float or wade. Large streamers for musky.",35.601,-82.551,"French Broad — Hominy Creek Park","Asheville Fly Fishing Co.","Asheville, NC",4.8],
[205,"Tuckasegee River","NC","Jackson Co.",35.38,-83.22,"03504000",["fly","spin"],["Rainbow Trout","Brown Trout"],"easy","moderate",100,500,"Delayed harvest water through Dillsboro. Popular year-round.","Delayed Harvest — check NCWRC","Wooly buggers in fall. Sulphurs spring/summer.",35.381,-83.221,"Tuckasegee — Dillsboro access","Fly Fishing the Smokies","Bryson City, NC",4.8],
[301,"Clinch River (TN)","TN","Anderson Co.",36.25,-83.65,"03540500",["fly","spin"],["Rainbow Trout","Brown Trout"],"easy","moderate",200,1500,"World-class tailwater below Norris Dam.","Special regs — check TWRA","Midges and Sulphurs dominate. Streamers in fall.",36.251,-83.649,"Clinch — Miller Island access","Little River Outfitters","Townsend, TN",4.9],
[302,"Little River (TN)","TN","Great Smoky Mountains",35.65,-83.75,"03460000",["fly"],["Rainbow Trout","Brook Trout","Brown Trout"],"easy","high",100,600,"Iconic GSMNP stream. Wild rainbow and brook trout.","GSMNP — artificial barbless, C&R","Small dries and nymphs. Fish early.",35.651,-83.749,"Little River — Metcalf Bottoms","Little River Outfitters","Townsend, TN",4.9],
[303,"South Holston River","TN","Sullivan Co.",36.52,-82.28,"03488000",["fly"],["Rainbow Trout","Brown Trout"],"easy","moderate",100,700,"Trico hatch capital of the Southeast.","Special regs — check TWRA","Trico hatches July–Oct. 7x tippet essential.",36.521,-82.279,"South Holston — Osceola Island","Watauga Angler","Elizabethton, TN",4.7],
[401,"Chattahoochee River","GA","Metro Atlanta",33.9,-84.4,"02335000",["fly","spin"],["Brown Trout","Rainbow Trout","Smallmouth Bass"],"easy","high",500,3000,"Tailwater below Buford Dam. One of the most-fished tailwaters in the US.","C&R Artificial Only in upper 2.5 miles","Midges and Sulphurs year-round.",33.901,-84.401,"Chattahoochee — Island Ford NRA","Orvis Atlanta","Atlanta, GA",4.8],
[402,"Toccoa River","GA","Fannin Co.",34.85,-84.25,"02329000",["fly","spin"],["Brown Trout","Rainbow Trout","Brook Trout"],"easy","moderate",80,400,"North GA tailwater below Blue Ridge Dam.","C&R section below dam — check GADNR","Sulphurs in May. Streamers for big browns.",34.851,-84.251,"Toccoa River — Tammen Park","Cohutta Fishing Company","Blue Ridge, GA",4.9],
[501,"Penns Creek","PA","Centre Co.",40.88,-77.38,"01548500",["fly"],["Brown Trout","Rainbow Trout","Brook Trout"],"easy","moderate",100,600,"Limestone spring creek. Green Drake hatch is legendary.","Catch & Release in special regulation section","Green Drake hatch in late May is world-class.",40.881,-77.381,"Penns Creek — Coburn access","Feathered Hook","Coburn, PA",4.9],
[502,"Yellow Breeches Creek","PA","Cumberland Co.",40.1,-77.05,"01570500",["fly"],["Brown Trout","Rainbow Trout"],"easy","high",80,400,"Classic Cumberland Valley limestone creek.","Trophy Trout C&R in designated section","Sulphurs and Tricos.",40.101,-77.051,"Yellow Breeches — Boiling Springs","Yellow Breeches Outfitters","Carlisle, PA",4.8],
[503,"Delaware River (PA)","PA","Northeastern PA",41.25,-74.95,"01438500",["fly","spin"],["Brown Trout","Rainbow Trout","Smallmouth Bass","Shad"],"easy","moderate",3000,25000,"Upper Delaware wild and scenic. World-class wild brown trout.","C&R Fly Fishing Only in designated sections","Green Drake hatch in June is legendary.",41.251,-74.951,"Delaware River — Buckingham PA","Dette Trout Flies","Livingston Manor, NY",4.8],
[601,"Beaverkill River","NY","Catskills",41.85,-74.95,"01421618",["fly"],["Brown Trout","Rainbow Trout","Brook Trout"],"easy","high",100,600,"The cradle of American fly fishing. Historic Catskill trout stream.","C&R Fly Fishing Only in upper section","Green Drake in June. Hendricksons in April.",41.851,-74.951,"Beaverkill — Roscoe access","Dette Trout Flies","Roscoe, NY",4.9],
[602,"Ausable River","NY","Adirondacks",44.4,-73.82,"04275000",["fly","spin"],["Brown Trout","Rainbow Trout","Brook Trout"],"easy","moderate",200,1500,"Adirondack treasure. Ausable Wulff originated here.","Special C&R section in Wilmington Notch","Stimulators and Wulffs. Early June Hendricksons.",44.401,-73.821,"Ausable River — Wilmington","The Hungry Trout","Wilmington, NY",4.8],
[603,"Salmon River (NY)","NY","Oswego Co.",43.55,-76.15,"04249000",["fly","spin"],["Steelhead","Chinook Salmon","Coho Salmon","Brown Trout"],"easy","high",200,2000,"New York's premier salmon and steelhead river.","Special regulations — NYSDEC seasonal","October chinook run is massive.",43.551,-76.151,"Salmon River — Pulaski","Whitaker's Sport Store","Pulaski, NY",4.8],
[701,"Greenbrier River","WV","Pocahontas Co.",38.12,-80.28,"03183500",["fly","spin"],["Brown Trout","Rainbow Trout","Brook Trout","Smallmouth Bass"],"easy","moderate",200,1500,"WV's premier trout river. Trout above Caldwell, smallmouth below.","Special Trout Management Areas — check WVDNR","Green Drakes in June. Float from Marlinton.",38.121,-80.281,"Greenbrier River — Marlinton","Greenbrier River Outfitters","Caldwell, WV",4.8],
[801,"Battenkill (VT)","VT","Bennington Co.",43.18,-73.22,"01313500",["fly"],["Brown Trout","Brook Trout"],"easy","moderate",80,500,"One of America's most famous trout streams. Selective wild brown trout.","Special C&R fly-fishing section — check VTFWD","Very selective fish. 6x–7x tippet required.",43.181,-73.221,"Battenkill — Arlington access","Orvis Flagship","Manchester, VT",4.9],
[901,"South Platte River","CO","Park / Jefferson Co.",39.22,-105.52,"06696980",["fly"],["Brown Trout","Rainbow Trout"],"easy","high",50,300,"Cheesman Canyon — one of the most challenging dry-fly fisheries in the US.","Gold Medal C&R Artificial Only","Size 22–26 midges and Baetis. 7x tippet mandatory.",39.221,-105.521,"South Platte — Deckers access","Trouts Fly Fishing","Denver, CO",4.9],
[902,"Madison River","MT","Madison Co.",44.98,-111.5,"06038800",["fly"],["Brown Trout","Rainbow Trout","Cutthroat Trout"],"easy","moderate",1000,6000,"One of the world's great trout rivers. Exits Yellowstone NP.","Special regulations — check MT FWP","Salmonfly hatch in June is legendary.",44.981,-111.501,"Madison River — Quake Lake","Bob Jacklin's Fly Shop","West Yellowstone, MT",4.9],
[903,"Henry's Fork","ID","Fremont Co.",44.35,-111.48,"13053800",["fly"],["Rainbow Trout","Brown Trout","Cutthroat Trout"],"easy","high",500,3000,"Box Canyon and Railroad Ranch — birthplace of modern Western dry fly fishing.","Special C&R and artificial-only — check IDFG","PMD and Green Drake hatches in June–July.",44.351,-111.481,"Henry's Fork — Last Chance ID","Trouthunter","Last Chance, ID",4.9],
[904,"Deschutes River","OR","Sherman Co.",45.42,-120.88,"14092750",["fly"],["Rainbow Trout","Brown Trout","Summer Steelhead"],"easy","moderate",1000,6000,"Oregon's most famous trout river. Redsides rainbow trout and summer steelhead.","Special regulations — fly only in Locked Gate section","Salmonfly hatch in late May is world-class.",45.421,-120.881,"Deschutes River — Maupin access","The Deschutes Angler","Maupin, OR",4.9],
[905,"San Juan River (NM)","NM","San Juan Co.",36.78,-107.68,"09368000",["fly"],["Rainbow Trout","Brown Trout"],"easy","high",200,1500,"Four Corners tailwater below Navajo Dam. World-class midge fishery.","Quality Waters — C&R Fly Fishing Only in upper 3.75 miles","Midges size 24-28. Huge fish on tiny flies.",36.781,-107.681,"San Juan River — Navajo Dam","Rizuto's San Juan Lodge","Navajo Dam, NM",4.9],
];

const ALL_SPOTS=RAW.filter(r=>Array.isArray(r)&&r.length===22).map(([id,name,state,region,lat,lng,usgsGauge,type,species,access,popProximity,flowMin,flowMax,description,regulations,tips,accessLat,accessLng,accessLabel,shopName,shopAddr,shopRating])=>
  ({id,name,state,region,lat,lng,usgsGauge,type,species,access,popProximity,flowRange:[flowMin,flowMax],description,regulations,tips,accessLat,accessLng,accessLabel,shop:{name:shopName,address:shopAddr,rating:shopRating}})
);

const ALL_SPECIES=[...new Set(ALL_SPOTS.flatMap(s=>s.species))].sort();
const ALL_STATES=[...new Set(ALL_SPOTS.map(s=>s.state))].sort();
const MONTHS=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const HATCH_BASE={
  1:{fly:[{n:"Zebra Midge",d:"#20-24 black/red wire"},{n:"Mercury Midge",d:"#22-26 silver bead"},{n:"BWO Parachute",d:"#18-22"},{n:"Jujubee Midge",d:"#20-24 gray/black"},{n:"Griffith's Gnat",d:"#18-22"}],spin:[{n:"Trout Magnet Jig",d:"1/64oz white/chartreuse"},{n:"Panther Martin #0",d:"1/32oz gold"},{n:"Micro Kastmaster",d:"1/8oz silver"}]},
  2:{fly:[{n:"Zebra Midge",d:"#20-24"},{n:"Early Black Stonefly",d:"#14-16"},{n:"Pheasant Tail",d:"#16-18 bead"},{n:"RS2",d:"#20-22 gray"},{n:"Perdigon",d:"#16-18 dark"}],spin:[{n:"Rooster Tail #0",d:"1/16oz white"},{n:"Panther Martin #2",d:"gold/black"},{n:"Acme Little Cleo",d:"1/4oz gold"}]},
  3:{fly:[{n:"Quill Gordon",d:"#14"},{n:"Hendrickson",d:"#12-14 pinkish-tan"},{n:"Early Black Stonefly",d:"#14-16"},{n:"Hare's Ear Nymph",d:"#12-16 gold bead"},{n:"Elk Hair Caddis",d:"#14-16 tan"}],spin:[{n:"Mepps Aglia #1",d:"gold"},{n:"Rooster Tail #2",d:"yellow/chartreuse"},{n:"Blue Fox Vibrax #2",d:"silver/blue"}]},
  4:{fly:[{n:"Hendrickson",d:"#12-14"},{n:"March Brown",d:"#10-12"},{n:"Copper John",d:"#14-16 red"},{n:"Elk Hair Caddis",d:"#14-16 olive"},{n:"Soft Hackle PT",d:"#14-16"}],spin:[{n:"Mepps Aglia #2",d:"gold/silver"},{n:"Rapala Original F5",d:"gold/black"},{n:"Panther Martin #4",d:"yellow spotted"}]},
  5:{fly:[{n:"Sulphur Comparadun",d:"#16-18 yellow-orange"},{n:"Green Drake",d:"#10-12 olive"},{n:"Elk Hair Caddis",d:"#14-16 tan"},{n:"Sulphur Nymph",d:"#16-18"},{n:"PMD Sparkle Dun",d:"#16-18"}],spin:[{n:"Panther Martin #4",d:"gold/black"},{n:"Kastmaster 1/4oz",d:"silver"},{n:"Mepps Aglia #3",d:"gold"}]},
  6:{fly:[{n:"Sulphur Parachute",d:"#16-18"},{n:"Salmonfly",d:"#2-4 orange"},{n:"Yellow Sally",d:"#14-16 yellow"},{n:"Foam Beetle",d:"#14-16"},{n:"PMD Comparadun",d:"#16-18"}],spin:[{n:"Rapala Shad Rap #5",d:"silver/black"},{n:"Blue Fox Vibrax #3",d:"gold"},{n:"Rebel Wee Crawfish",d:"brown/orange"}]},
  7:{fly:[{n:"Trico Spent Spinner",d:"#20-24"},{n:"Dave's Hopper",d:"#8-12 yellow"},{n:"PMD Parachute",d:"#16-18"},{n:"Black Ant",d:"#18-20"},{n:"Crystal Bugger",d:"#6-8 olive/black"}],spin:[{n:"Thomas Buoyant",d:"1/4oz gold"},{n:"Rapala F7",d:"rainbow"},{n:"Kastmaster",d:"1/4oz silver/blue"}]},
  8:{fly:[{n:"Trico Parachute",d:"#22-24"},{n:"Parachute Hopper",d:"#10-14"},{n:"Cinnamon Ant",d:"#18-20"},{n:"Foam Beetle",d:"#14-16"},{n:"Clouser Minnow",d:"#6-8 chartreuse"}],spin:[{n:"Kastmaster 1/4oz",d:"gold"},{n:"Mepps Aglia #3",d:"silver/red"},{n:"Rebel Pop-R",d:"P60"}]},
  9:{fly:[{n:"BWO Sparkle Dun",d:"#16-20"},{n:"Mahogany Dun",d:"#14-16"},{n:"Elk Hair Caddis",d:"#14-18"},{n:"Woolly Bugger",d:"#4-6 olive/black"},{n:"Muddler Minnow",d:"#6-8"}],spin:[{n:"Rapala F7",d:"brown trout"},{n:"Cyclone Spoon",d:"1/4oz silver/red"},{n:"Mepps Aglia #3",d:"gold"}]},
  10:{fly:[{n:"BWO Comparadun",d:"#16-20"},{n:"Slate Drake",d:"#10-12"},{n:"Articulated Streamer",d:"#2-6 olive/white"},{n:"Egg Pattern",d:"#10-12 orange"},{n:"Zebra Midge",d:"#20-22"}],spin:[{n:"Kastmaster",d:"gold/orange"},{n:"Rapala Husky Jerk",d:"HJ6 brown trout"},{n:"Z-Ray",d:"red/gold"}]},
  11:{fly:[{n:"Zebra Midge",d:"#20-24"},{n:"Mercury Black Beauty",d:"#20-22"},{n:"BWO Parachute",d:"#18-22"},{n:"Woolly Bugger",d:"#4-6 olive"},{n:"Egg Pattern",d:"#10-12"}],spin:[{n:"Trout Magnet",d:"1/64oz pink/white"},{n:"Micro Spoon",d:"1/8oz silver"},{n:"Rooster Tail #0",d:"white"}]},
  12:{fly:[{n:"Zebra Midge",d:"#22-26"},{n:"Jujubee Midge",d:"#20-24 gray/black"},{n:"Blood Midge",d:"#20-24 red wire"},{n:"Egg Pattern",d:"#10-12 chartreuse"},{n:"Barr's Emerger",d:"#18-20"}],spin:[{n:"Trout Magnet",d:"1/64oz white"},{n:"Acme Little Cleo",d:"1/8oz gold"},{n:"Micro Kastmaster",d:"1/8oz silver"}]},
};

const getHatch=(state,month)=>HATCH_BASE[month]||HATCH_BASE[1];
const PRESSURE={low:18,moderate:10,high:4};
const ACCESS_SC={easy:5,moderate:10,hard:15};

function dist(a,b,c,d){const R=3958.8,dL=(c-a)*Math.PI/180,dG=(d-b)*Math.PI/180,x=Math.sin(dL/2)**2+Math.cos(a*Math.PI/180)*Math.cos(c*Math.PI/180)*Math.sin(dG/2)**2;return R*2*Math.atan2(Math.sqrt(x),Math.sqrt(1-x));}

async function zipToCoords(zip){
  try{const r=await fetch(`https://api.zippopotam.us/us/${zip}`);if(r.ok){const d=await r.json();const p=d?.places?.[0];if(p)return{lat:parseFloat(p.latitude),lng:parseFloat(p.longitude),city:`${p["place name"]}, ${p["state abbreviation"]}`};}}catch{}return null;
}

async function fetchFlowsBatch(gauges){
  const results={};
  try{
    const ids=gauges.join(",");
    const url=`https://waterservices.usgs.gov/nwis/iv/?format=json&sites=${ids}&parameterCd=00060&siteStatus=active`;
    const r=await fetch(url,{signal:AbortSignal.timeout(12000)});
    if(!r.ok)return results;
    const d=await r.json();
    const series=d?.value?.timeSeries||[];
    series.forEach(ts=>{
      const siteNo=ts?.sourceInfo?.siteCode?.[0]?.value;
      const val=ts?.values?.[0]?.value?.[0]?.value;
      if(siteNo&&val&&!isNaN(parseFloat(val))){results[siteNo]=parseFloat(val);}
    });
  }catch(e){console.warn("USGS batch fetch error",e);}
  return results;
}

async function fetchWx(lat,lng){
  try{
    const r=await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code,wind_speed_10m,surface_pressure,cloud_cover&temperature_unit=fahrenheit&wind_speed_unit=mph&forecast_days=1`,{signal:AbortSignal.timeout(8000)});
    if(!r.ok)return null;
    const d=await r.json();const c=d.current;
    const w=code=>code===0?"clear sky":code<=3?"partly cloudy":code<=49?"foggy":code<=69?"rain":code<=82?"showers":"thunderstorm";
    return{temp:Math.round(c.temperature_2m),desc:w(c.weather_code),pressure:c.surface_pressure,windSpeed:Math.round(c.wind_speed_10m),clouds:c.cloud_cover};
  }catch{return null;}
}

function wxScore(wx){if(!wx)return 10;let s=10;if(wx.temp>=45&&wx.temp<=65)s+=5;else if(wx.temp<35||wx.temp>80)s-=5;if(wx.pressure>=1010&&wx.pressure<=1020)s+=2;else if(wx.pressure<1000)s-=3;if(wx.windSpeed<10)s+=2;if(wx.desc?.includes("rain")||wx.desc?.includes("thunder"))s-=4;if(wx.clouds>70)s+=2;return Math.max(0,Math.min(20,s));}

function scoreSpot(spot,hour,type,selSpecies,flows,wx,wkMult,userCoords){
  if(!spot.type.includes(type))return null;
  if(selSpecies.length>0&&!spot.species.some(s=>selSpecies.includes(s)))return null;
  const sc={};
  sc.weather=wxScore(wx[spot.id]);
  const flowCfs=flows[spot.usgsGauge];
  let fs=12;
  if(flowCfs!=null){const[mn,mx]=spot.flowRange;const mid=(mn+mx)/2;const range=Math.max(mx-mn,1);const pct=1-Math.min(1,Math.abs(flowCfs-mid)/(range/2));fs=Math.max(0,Math.round(pct*20));if(flowCfs<mn*0.5||flowCfs>mx*2)fs=Math.max(0,fs-8);}
  sc.flow=fs;
  sc.pressure=Math.min(20,Math.round((PRESSURE[spot.popProximity]+ACCESS_SC[spot.access]/2)*wkMult));
  sc.feeding=(hour>=6&&hour<=9)||(hour>=17&&hour<=20)?16:hour>=10&&hour<=15?8:3;
  let prox=5;
  if(userCoords){const d=dist(userCoords.lat,userCoords.lng,spot.lat,spot.lng);prox=d<30?10:d<60?8:d<100?6:d<150?4:d<250?2:0;}
  sc.proximity=prox;
  const total=Object.values(sc).reduce((a,b)=>a+b,0);
  const spotDist=userCoords?Math.round(dist(userCoords.lat,userCoords.lng,spot.lat,spot.lng)):null;
  return{...spot,score:Math.min(99,Math.round(total)),scores:sc,liveFlow:flowCfs!=null?{cfs:flowCfs}:null,wx:wx[spot.id],dist:spotDist};
}

// ─── FISHING APP UI COMPONENTS ────────────────────────────────────────────────
const scoreColor=s=>s>=86?"#4ade80":s>=71?C.sand:s>=51?C.orange:s>=36?"#ef4444":C.scoreLow;
const ScoreRing=({score,sz=52})=>{
  const col=scoreColor(score);
  return <div style={{width:sz,height:sz,borderRadius:"50%",border:`2.5px solid ${col}`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",flexShrink:0,background:`${col}22`}}>
    <span style={{fontSize:sz*0.32,fontWeight:800,color:col,lineHeight:1}}>{score}</span>
    <span style={{fontSize:9,color:C.textMuted}}>score</span>
  </div>;
};
const Bar=({label,value,max,color})=>(
  <div style={{marginBottom:5}}>
    <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:C.textSecondary,marginBottom:1}}><span>{label}</span><span style={{color,fontWeight:600}}>{Math.round(value)}/{max}</span></div>
    <div style={{height:5,background:C.navyMid,borderRadius:4}}><div style={{height:"100%",width:`${Math.min(100,(value/max)*100)}%`,background:color,borderRadius:4,transition:"width 0.5s"}}/></div>
  </div>
);
const FlyTag=({item})=>(
  <div style={{background:`${C.orangeLight}15`,border:`1px solid ${C.orange}44`,borderRadius:7,padding:"5px 9px",marginBottom:4}}>
    <span style={{color:C.orangeLight,fontWeight:700,fontSize:12}}>{item.n}</span>
    <span style={{color:C.textSecondary,fontSize:11}}> — {item.d}</span>
  </div>
);
const StateBadge=({state})=><span style={{fontSize:10,padding:"2px 7px",borderRadius:4,fontWeight:700,background:`${STATE_COLORS[state]||"#888"}22`,border:`1px solid ${STATE_COLORS[state]||"#888"}55`,color:STATE_COLORS[state]||"#888"}}>{state}</span>;
const AppBtn=({active,onClick,children,ac=C.orange,style={}})=>(
  <button onClick={onClick} style={{padding:"5px 10px",fontSize:11,borderRadius:6,border:"1px solid",cursor:"pointer",background:active?`${ac}25`:C.navyMid,borderColor:active?ac:C.border,color:active?ac:C.textSecondary,fontWeight:active?700:400,...style}}>{children}</button>
);


const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;


function MapView({ results, selected, onSelect }) {
  const mapRef = useRef();

  // Fly to spot when selected
  const handleMarkerClick = useCallback((e, spot) => {
    e.originalEvent.stopPropagation();
    const isSel = selected === spot.id;
    onSelect(isSel ? null : spot.id);
    if (!isSel) {
      mapRef.current?.flyTo({
        center: [spot.lng, spot.lat],
        zoom: 11,
        duration: 800,
      });
    }
  }, [selected, onSelect]);

  return (
    <div style={{ position: "relative", borderRadius: 12, overflow: "hidden", height: 400 }}>
      <Map
        ref={mapRef}
        mapboxAccessToken={TOKEN}
        mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
        initialViewState={{
          longitude: -96,
          latitude: 40,
          zoom: 3.5,
        }}
        style={{ width: "100%", height: "100%" }}
      >
        <NavigationControl position="top-left" showCompass={false} />

        {results.map(spot => {
          const isSel = selected === spot.id;
          const color = scoreColor(spot.score);
          return (
            <Marker
              key={spot.id}
              longitude={spot.lng}
              latitude={spot.lat}
              anchor="center"
              onClick={e => handleMarkerClick(e, spot)}
            >
              <div style={{ position: "relative", cursor: "pointer" }}>
                {/* Outer glow ring */}
                <div style={{
                  position: "absolute",
                  inset: isSel ? -8 : -4,
                  borderRadius: "50%",
                  background: color,
                  opacity: 0.25,
                  pointerEvents: "none",
                }} />
                {/* Core dot */}
                <div style={{
                  width: isSel ? 16 : 8,
                  height: isSel ? 16 : 8,
                  borderRadius: "50%",
                  background: color,
                  border: "1.5px solid #0a0e1a",
                  boxShadow: isSel ? `0 0 0 2px ${color}55` : "none",
                  transition: "all 0.15s ease",
                }} />
                {/* Name label when selected */}
                {isSel && (
                  <div style={{
                    position: "absolute",
                    bottom: "calc(100% + 5px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "rgba(10,14,26,0.92)",
                    color: "#fff",
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 7px",
                    borderRadius: 5,
                    whiteSpace: "nowrap",
                    pointerEvents: "none",
                    textShadow: "0 1px 3px rgba(0,0,0,0.9)",
                    border: `1px solid ${color}66`,
                  }}>
                    {spot.name.substring(0, 22)}
                  </div>
                )}
              </div>
            </Marker>
          );
        })}
      </Map>

      {/* Legend — matches your existing style */}
      <div style={{
        position: "absolute", bottom: 8, right: 8,
        display: "flex", gap: 4, flexDirection: "column",
      }}>
        {[
          ["#4ade80", "86-100 Hot"],
          ["#e2c97e", "71-85 Good"],
          ["#e8784d", "51-70 Fair"],
          ["#ef4444", "36-50 Poor"],
          ["#6b7280", "<36 Slow"],
        ].map(([col, l]) => (
          <div key={l} style={{
            display: "flex", alignItems: "center", gap: 4,
            background: "rgba(0,0,0,0.65)", backdropFilter: "blur(4px)",
            borderRadius: 5, padding: "2px 7px",
          }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: col }} />
            <span style={{ fontSize: 9, color: "#fff" }}>{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function FishingApp(){
  const now=new Date();
  const [month,setMonth]=useState(now.getMonth()+1);
  const [day,setDay]=useState(now.getDate());
  const [hour,setHour]=useState(now.getHours());
  const [type,setType]=useState("fly");
  const [stateFilter,setStateFilter]=useState("ALL");
  const [regionFilter,setRegionFilter]=useState("ALL");
  const [selSpecies,setSelSpecies]=useState([]);
  const [view,setView]=useState("list");
  const [expanded,setExpanded]=useState(null);
  const [mapSel,setMapSel]=useState(null);
  const [flows,setFlows]=useState({});
  const [wxData,setWxData]=useState({});
  const [loading,setLoading]=useState(true);
  const [results,setResults]=useState([]);
  const [apiStatus,setApiStatus]=useState({usgs:"loading",wx:"loading"});
  const [lastFetch,setLastFetch]=useState(null);
  const [userCoords,setUserCoords]=useState(null);
  const [zipInput,setZipInput]=useState("");
  const [zipLoading,setZipLoading]=useState(false);
  const [locLabel,setLocLabel]=useState("");
  const [showSpecies,setShowSpecies]=useState(false);
  const [showStates,setShowStates]=useState(false);

  const fetchAll=useCallback(async()=>{
    setLoading(true);
    const allGauges=[...new Set(ALL_SPOTS.map(s=>s.usgsGauge))].filter(g=>g&&g!=="N/A");
    const newFlows={};
    const BATCH=100,CONCURRENCY=8;
    const chunks=[];
    for(let i=0;i<allGauges.length;i+=BATCH) chunks.push(allGauges.slice(i,i+BATCH));
    for(let i=0;i<chunks.length;i+=CONCURRENCY){
      const results=await Promise.all(chunks.slice(i,i+CONCURRENCY).map(fetchFlowsBatch));
      results.forEach(b=>Object.assign(newFlows,b));
    }
    setFlows(newFlows);
    setApiStatus(p=>({...p,usgs:Object.keys(newFlows).length>0?"live":"no data"}));
    const clusters=[
      {lat:38.5,lng:-79.0},{lat:37.0,lng:-80.5},{lat:36.7,lng:-82.0},{lat:38.8,lng:-78.5},
      {lat:35.5,lng:-83.5},{lat:36.0,lng:-86.5},{lat:33.9,lng:-84.4},{lat:40.5,lng:-77.5},
      {lat:42.5,lng:-74.5},{lat:43.2,lng:-73.2},{lat:38.5,lng:-80.0},
      {lat:39.5,lng:-105.5},{lat:44.9,lng:-111.5},{lat:44.4,lng:-114.0},{lat:45.4,lng:-120.8},
      {lat:36.8,lng:-107.7},{lat:39.0,lng:-111.5}
    ];
    const wxRes=await Promise.all(clusters.map(c=>fetchWx(c.lat,c.lng)));
    const newWx={};
    ALL_SPOTS.forEach(spot=>{
      let bestIdx=0,minD=Infinity;
      clusters.forEach((c,i)=>{const d=Math.abs(c.lat-spot.lat)+Math.abs(c.lng-spot.lng);if(d<minD){minD=d;bestIdx=i;}});
      if(wxRes[bestIdx])newWx[spot.id]=wxRes[bestIdx];
    });
    setWxData(newWx);
    setApiStatus(p=>({...p,wx:Object.values(newWx).some(v=>v)?"live":"no data"}));
    setLastFetch(new Date());
    setLoading(false);
  },[]);

  useEffect(()=>{fetchAll();const t=setInterval(fetchAll,3600000);return()=>clearInterval(t);},[fetchAll]);

  const daysInMonth=new Date(now.getFullYear(),month,0).getDate();
  const jsDay=new Date(now.getFullYear(),month-1,day).getDay();
  const onWkend=jsDay===0||jsDay===6;
  const wkMult=onWkend?0.75:1.0;

  useEffect(()=>{
    let spots=ALL_SPOTS;
    if(stateFilter!=="ALL")spots=spots.filter(s=>s.state===stateFilter);
    else if(regionFilter==="East")spots=spots.filter(s=>["VA","MD","PA","NY","ME","VT","NH","WV"].includes(s.state));
    else if(regionFilter==="SE")spots=spots.filter(s=>["NC","TN","GA","KY"].includes(s.state));
    else if(regionFilter==="West")spots=spots.filter(s=>["CO","TX","NM","AZ","UT","ID","MT","WY","WA","OR","CA"].includes(s.state));
    const scored=spots.map(s=>scoreSpot(s,hour,type,selSpecies,flows,wxData,wkMult,userCoords)).filter(Boolean).sort((a,b)=>b.score-a.score);
    setResults(scored);
  },[month,hour,type,stateFilter,regionFilter,selSpecies,flows,wxData,wkMult,userCoords]);

  const handleGeo=()=>{if(!navigator.geolocation)return;navigator.geolocation.getCurrentPosition(p=>{setUserCoords({lat:p.coords.latitude,lng:p.coords.longitude});setLocLabel("📍 Your location");},()=>setLocLabel("Unavailable"));};
  const handleZip=async()=>{if(zipInput.length!==5)return;setZipLoading(true);const c=await zipToCoords(zipInput);if(c){setUserCoords({lat:c.lat,lng:c.lng});setLocLabel(`📍 ${c.city||zipInput}`);}else setLocLabel("ZIP not found");setZipLoading(false);};
  const toggleSp=sp=>setSelSpecies(p=>p.includes(sp)?p.filter(x=>x!==sp):[...p,sp]);
  const fmtHr=h=>h===0?"12 AM":h<12?`${h} AM`:h===12?"12 PM":`${h-12} PM`;
  const hatch=getHatch(stateFilter==="ALL"?"VA":stateFilter,month);
  const flies=type==="fly"?hatch.fly:hatch.spin;

  return(
    <div style={{minHeight:"100vh",background:C.navy,color:C.textPrimary,fontFamily:"system-ui,-apple-system,sans-serif"}}>
      <div style={{background:`linear-gradient(180deg,${C.navyLight} 0%,${C.navy} 100%)`,borderBottom:`1px solid ${C.border}`,padding:"12px 16px"}}>
        <div style={{maxWidth:740,margin:"0 auto",display:"flex",alignItems:"center",gap:12}}>
          <div style={{flex:1}}>
            <h1 style={{margin:0,fontSize:24,fontWeight:900,color:C.sand}}>🎣 Fishing Intelligence</h1>
            <div style={{display:"flex",gap:10,flexWrap:"wrap",marginTop:4}}>
              <span style={{fontSize:10,color:apiStatus.usgs==="live"?"#4ade80":C.sand}}>● USGS: {apiStatus.usgs}</span>
              <span style={{fontSize:10,color:apiStatus.wx==="live"?"#4ade80":C.sand}}>● Weather: {apiStatus.wx}</span>
              {lastFetch&&<span style={{fontSize:10,color:C.textMuted}}>Updated {lastFetch.toLocaleTimeString()}</span>}
            </div>
          </div>
          <button onClick={fetchAll} style={{background:`${C.orange}22`,border:`1px solid ${C.orange}66`,color:C.orange,borderRadius:8,padding:"6px 12px",fontSize:12,cursor:"pointer",fontWeight:700}}>↻ Refresh</button>
        </div>
      </div>
      <div style={{background:C.navyMid,borderBottom:`1px solid ${C.border}`,padding:"10px 16px",position:"sticky",top:0,zIndex:20}}>
        <div style={{maxWidth:740,margin:"0 auto"}}>
          <div style={{display:"flex",gap:7,flexWrap:"wrap",alignItems:"flex-end",marginBottom:7}}>
            {[["Month",<select value={month} onChange={e=>{setMonth(Number(e.target.value));setDay(1);}} style={{background:C.navy,border:`1px solid ${C.border}`,color:C.white,borderRadius:6,padding:"5px 7px",fontSize:12}}>
              {MONTHS.map((m,i)=><option key={i} value={i+1}>{m}</option>)}</select>],
            ["Day",<select value={day} onChange={e=>setDay(Number(e.target.value))} style={{background:C.navy,border:`1px solid ${C.border}`,color:C.white,borderRadius:6,padding:"5px 7px",fontSize:12}}>
              {Array.from({length:daysInMonth},(_,i)=>i+1).map(d=><option key={d} value={d}>{d}</option>)}</select>],
            ["Time",<select value={hour} onChange={e=>setHour(Number(e.target.value))} style={{background:C.navy,border:`1px solid ${C.border}`,color:C.white,borderRadius:6,padding:"5px 7px",fontSize:12}}>
              {Array.from({length:24},(_,i)=><option key={i} value={i}>{fmtHr(i)}</option>)}</select>]].map(([lbl,el])=>(
              <div key={lbl}><div style={{fontSize:9,color:C.textMuted,marginBottom:2,textTransform:"uppercase"}}>{lbl}</div>{el}</div>
            ))}
            <div>
              <div style={{fontSize:9,color:C.textMuted,marginBottom:2,textTransform:"uppercase"}}>Technique</div>
              <div style={{display:"flex",gap:3}}>
                <AppBtn active={type==="fly"} onClick={()=>setType("fly")}>🪰 Fly</AppBtn>
                <AppBtn active={type==="spin"} onClick={()=>setType("spin")}>🌀 Spin</AppBtn>
              </div>
            </div>
            <div style={{marginLeft:"auto"}}>
              <div style={{fontSize:9,color:C.textMuted,marginBottom:2,textTransform:"uppercase"}}>View</div>
              <div style={{display:"flex",gap:3}}>
                <AppBtn active={view==="list"} onClick={()=>setView("list")} ac={C.sand}>☰</AppBtn>
                <AppBtn active={view==="map"} onClick={()=>setView("map")} ac={C.sand}>🗺</AppBtn>
              </div>
            </div>
          </div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap",alignItems:"center"}}>
            {["ALL","East","SE","West"].map(r=>(
              <AppBtn key={r} active={regionFilter===r&&stateFilter==="ALL"} onClick={()=>{setRegionFilter(r);setStateFilter("ALL");}} ac={C.sand}>{r}</AppBtn>
            ))}
            <div style={{position:"relative"}}>
              <AppBtn active={stateFilter!=="ALL"} onClick={()=>setShowStates(p=>!p)} ac={STATE_COLORS[stateFilter]||C.sand}>
                {stateFilter==="ALL"?"State ▾":stateFilter+" ✕"}
              </AppBtn>
              {showStates&&(
                <div style={{position:"absolute",top:"calc(100%+4px)",left:0,background:C.cardBg,border:`1px solid ${C.border}`,borderRadius:10,padding:10,zIndex:30,display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:3,width:210,boxShadow:"0 8px 40px #000"}}>
                  <button onClick={()=>{setStateFilter("ALL");setShowStates(false);}} style={{gridColumn:"1/-1",background:`${C.slate}22`,border:`1px solid ${C.border}`,color:C.textMuted,borderRadius:5,padding:"4px",fontSize:11,cursor:"pointer",marginBottom:4}}>All States</button>
                  {ALL_STATES.map(s=>(
                    <button key={s} onClick={()=>{setStateFilter(s);setShowStates(false);}} style={{background:stateFilter===s?`${STATE_COLORS[s]||"#888"}25`:"transparent",border:`1px solid ${stateFilter===s?STATE_COLORS[s]||"#888":C.border}`,color:stateFilter===s?STATE_COLORS[s]||"#888":C.textSecondary,borderRadius:5,padding:"4px",fontSize:11,cursor:"pointer",fontWeight:stateFilter===s?700:400}}>{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div style={{position:"relative"}}>
              <AppBtn active={selSpecies.length>0} onClick={()=>setShowSpecies(p=>!p)} ac={C.sand}>🐟 {selSpecies.length>0?`(${selSpecies.length})`:"▾"}</AppBtn>
              {showSpecies&&(
                <div style={{position:"absolute",top:"calc(100%+4px)",left:0,background:C.cardBg,border:`1px solid ${C.border}`,borderRadius:10,padding:10,zIndex:30,minWidth:220,maxHeight:260,overflowY:"auto",boxShadow:"0 8px 40px #000"}}>
                  {ALL_SPECIES.map(sp=>(
                    <div key={sp} onClick={()=>toggleSp(sp)} style={{display:"flex",alignItems:"center",gap:7,padding:"5px 7px",borderRadius:6,cursor:"pointer",background:selSpecies.includes(sp)?`${C.sand}18`:"transparent"}}>
                      <div style={{width:14,height:14,borderRadius:3,border:`2px solid ${selSpecies.includes(sp)?C.sand:C.border}`,background:selSpecies.includes(sp)?C.sand:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,color:C.navy,fontWeight:900}}>{selSpecies.includes(sp)&&"✓"}</div>
                      <span style={{fontSize:11,color:selSpecies.includes(sp)?C.sand:C.textSecondary}}>{sp}</span>
                    </div>
                  ))}
                  {selSpecies.length>0&&<button onClick={()=>setSelSpecies([])} style={{marginTop:6,width:"100%",background:C.navyMid,border:`1px solid ${C.border}`,color:C.textMuted,borderRadius:5,padding:"4px",fontSize:10,cursor:"pointer"}}>Clear</button>}
                </div>
              )}
            </div>
            <div style={{display:"flex",gap:5,alignItems:"center",marginLeft:"auto",flexWrap:"wrap"}}>
              {locLabel&&<span style={{fontSize:10,color:"#4ade80",fontWeight:600}}>{locLabel}</span>}
              <button onClick={handleGeo} style={{padding:"4px 8px",fontSize:10,borderRadius:5,border:`1px solid ${C.border}`,background:C.navyMid,color:C.textSecondary,cursor:"pointer"}}>📍</button>
              <input value={zipInput} onChange={e=>setZipInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleZip()} placeholder="ZIP" maxLength={5} style={{width:55,background:C.navy,border:`1px solid ${C.border}`,color:C.white,borderRadius:5,padding:"4px 6px",fontSize:11}}/>
              <button onClick={handleZip} disabled={zipLoading||zipInput.length!==5} style={{padding:"4px 8px",fontSize:10,borderRadius:5,border:`1px solid ${C.border}`,background:C.navyMid,color:C.textSecondary,cursor:"pointer"}}>{zipLoading?"…":"Go"}</button>
            </div>
          </div>
          {onWkend&&<div style={{marginTop:7,background:`${C.sand}15`,border:`1px solid ${C.sand}44`,borderRadius:7,padding:"4px 10px",fontSize:10,color:C.sand,fontWeight:600}}>⚠️ Weekend — pressure scores adjusted.</div>}
        </div>
      </div>
      <div style={{maxWidth:740,margin:"0 auto",padding:"12px 16px"}}>
        <div style={{background:C.cardBg,borderRadius:12,border:`1px solid ${C.border}`,padding:13,marginBottom:12}}>
          <div style={{fontSize:14,fontWeight:800,color:C.white,marginBottom:8}}>{type==="fly"?"🪰 Hatch Report":"🎯 Recommended Lures"} — {MONTHS[month-1]}</div>
          <div style={{display:"flex",flexDirection:"column",gap:3,marginBottom:8}}>{flies.map((f,i)=><FlyTag key={i} item={f}/>)}</div>
          <div style={{background:C.navyMid,borderRadius:7,padding:"7px 10px",fontSize:11,color:C.textSecondary,borderLeft:`3px solid ${C.orange}`}}>
            <strong style={{color:C.white}}>🐟 Feeding window: </strong>
            {hour>=6&&hour<=9?"Prime morning — emergers and dries near riffles.":hour>=17&&hour<=20?"Evening peak — spinner falls and caddis in fading light.":hour>=10&&hour<=15?"Midday lull — nymphs deep in shaded undercuts.":"Off-peak — streamers near structure."}
          </div>
        </div>
{view==="map"&&<div style={{marginBottom:12}}>
  <MapView results={results.slice(0,200)} selected={mapSel} onSelect={id=>{setMapSel(id);if(id)setExpanded(id);}}/>
  {mapSel&&(()=>{
    const spot=results.find(r=>r.id===mapSel);
    if(!spot)return null;
    const spotHatch=getHatch(spot.state,month);
    const spotFlies=type==="fly"?spotHatch.fly:spotHatch.spin;
    return(
      <div style={{marginTop:8,background:C.cardBg,borderRadius:11,border:`1px solid ${C.border}`,overflow:"hidden"}}>
        <div style={{padding:11,display:"flex",gap:9,alignItems:"center"}}>
          <ScoreRing score={spot.score}/>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
              <span style={{fontWeight:700,color:C.white,fontSize:13}}>{spot.name}</span>
              <StateBadge state={spot.state}/>
            </div>
            <div style={{fontSize:10,color:C.textMuted}}>{spot.region}{spot.dist!=null?` · ${spot.dist} mi`:""}</div>
            <div style={{display:"flex",gap:8,marginTop:2,flexWrap:"wrap"}}>
              <span style={{fontSize:10,color:spot.liveFlow?"#4ade80":C.textMuted}}>💧 {spot.liveFlow?`${spot.liveFlow.cfs.toFixed(0)} cfs`:"est."}</span>
              {spot.wx&&<span style={{fontSize:10,color:C.textSecondary}}>🌡 {spot.wx.temp}°F {spot.wx.desc}</span>}
            </div>
          </div>
        </div>
        <div style={{borderTop:`1px solid ${C.border}`,padding:13}}>
          <p style={{fontSize:11,color:C.textSecondary,margin:"0 0 10px",lineHeight:1.6}}>{spot.description}</p>
          <div style={{marginBottom:10}}>
            <Bar label="Weather" value={spot.scores.weather} max={20} color="#38bdf8"/>
            <Bar label="River Flow (USGS)" value={spot.scores.flow} max={20} color="#4ade80"/>
            <Bar label="Pressure / Access" value={spot.scores.pressure} max={20} color={C.sand}/>
            <Bar label="Feeding Window" value={spot.scores.feeding} max={16} color={C.orange}/>
            <Bar label="Proximity" value={spot.scores.proximity} max={10} color={C.orangeLight}/>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:10}}>
            <div style={{background:C.navyMid,borderRadius:7,padding:9,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:9,color:"#38bdf8",fontWeight:700,marginBottom:2}}>💧 FLOW</div>
              {spot.liveFlow
                ?<><div style={{fontSize:16,fontWeight:800,color:"#38bdf8"}}>{spot.liveFlow.cfs.toFixed(0)} <span style={{fontSize:10}}>cfs</span></div>
                  <div style={{fontSize:9,color:C.textMuted}}>Ideal: {spot.flowRange[0]}–{spot.flowRange[1]}</div>
                  <div style={{fontSize:10,fontWeight:600,color:spot.liveFlow.cfs>=spot.flowRange[0]&&spot.liveFlow.cfs<=spot.flowRange[1]?"#4ade80":C.sand}}>{spot.liveFlow.cfs>=spot.flowRange[0]&&spot.liveFlow.cfs<=spot.flowRange[1]?"✓ Ideal":"⚠ Out of range"}</div></>
                :<div style={{fontSize:10,color:C.textMuted}}>Live data unavailable<br/><span style={{fontSize:9}}>Ideal: {spot.flowRange[0]}–{spot.flowRange[1]} cfs</span>        </div>}

            </div>
            <div style={{background:C.navyMid,borderRadius:7,padding:9,border:`1px solid ${C.border}`}}>
              <div style={{fontSize:9,color:C.sand,fontWeight:700,marginBottom:2}}>🌤 WEATHER</div>
              {spot.wx
                ?<><div style={{fontSize:16,fontWeight:800,color:C.sand}}>{spot.wx.temp}°F</div>
                  <div style={{fontSize:10,color:C.textSecondary}}>{spot.wx.desc}</div>
                  <div style={{fontSize:9,color:C.textMuted}}>💨 {spot.wx.windSpeed} mph</div></>
                :<div style={{fontSize:10,color:C.textMuted}}>Unavailable</div>}
            </div>
          </div>
          <div style={{background:C.navyMid,borderRadius:7,padding:9,marginBottom:9,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:9,color:C.orangeLight,fontWeight:700,marginBottom:5,textTransform:"uppercase"}}>{type==="fly"?"🪰 Recommended Flies:":"🎯 Recommended Lures:"}</div>
            {spotFlies.map((f,i)=><FlyTag key={i} item={f}/>)}
          </div>
          <div style={{background:C.navyMid,borderRadius:7,padding:9,marginBottom:7,border:`1px solid ${C.border}`}}>
            <div style={{fontSize:9,color:C.sand,fontWeight:700,marginBottom:2}}>🏪 {spot.shop.name}</div>
            <div style={{fontSize:10,color:C.textSecondary}}>{spot.shop.address} · ★ {spot.shop.rating}</div>
          </div>
          <div style={{fontSize:10,color:C.textMuted,marginBottom:7}}>🐟 {spot.species.join(" · ")}</div>
          <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:9}}>
            <a href={`https://www.google.com/maps/dir/?api=1&destination=${spot.accessLat},${spot.accessLng}&travelmode=driving`} target="_blank" rel="noopener noreferrer" style={{fontSize:11,background:`${C.orange}22`,border:`1px solid ${C.orange}66`,color:C.orange,padding:"6px 12px",borderRadius:6,textDecoration:"none",fontWeight:700}}>🗺 Directions</a>
            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.shop.name+" "+spot.shop.address)}`} target="_blank" rel="noopener noreferrer" style={{fontSize:11,background:`${C.sand}22`,border:`1px solid ${C.sand}66`,color:C.sand,padding:"6px 12px",borderRadius:6,textDecoration:"none",fontWeight:700}}>🏪 Shop Map</a>
          </div>
          <div style={{fontSize:11,color:C.textSecondary,fontStyle:"italic",borderLeft:`3px solid ${C.border}`,paddingLeft:9,marginBottom:7}}>{spot.tips}</div>
          <div style={{fontSize:11,color:C.sand,fontWeight:600}}>📋 {spot.regulations}</div>
        </div>
      </div>
    );
  })()}
</div>}        
        {loading
          ?<div style={{textAlign:"center",padding:40,color:C.textMuted,fontSize:14}}>⏳ Fetching live USGS flows &amp; weather…</div>
          :<div>
            <div style={{fontSize:11,color:C.textMuted,marginBottom:8}}><span style={{color:C.sand,fontWeight:600}}>{results.length} spots</span> · {MONTHS[month-1]} · {fmtHr(hour)}</div>
            <div style={{display:"flex",flexDirection:"column",gap:7}}>
              {results.slice(0,80).map((spot,idx)=>{
                const isOpen=expanded===spot.id;
                const rankColor=idx===0?scoreColor(spot.score):idx===1?C.sand:idx===2?C.sandLight:C.border;
                const spotHatch=getHatch(spot.state,month);
                const spotFlies=type==="fly"?spotHatch.fly:spotHatch.spin;
                return(
                  <div key={spot.id} style={{background:C.cardBg,borderRadius:11,border:`1px solid ${isOpen?rankColor:C.border}`,overflow:"hidden"}}>
                    <div style={{padding:11,cursor:"pointer",display:"flex",gap:9,alignItems:"center"}} onClick={()=>setExpanded(isOpen?null:spot.id)}>
                      <div style={{fontSize:12,fontWeight:800,color:idx<3?rankColor:C.textMuted,width:22,textAlign:"center",flexShrink:0}}>#{idx+1}</div>
                      <ScoreRing score={spot.score}/>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                          <span style={{fontWeight:700,fontSize:13,color:C.white}}>{spot.name}</span>
                          <StateBadge state={spot.state}/>
                          {idx===0&&<span style={{fontSize:9,background:`${C.orange}25`,border:`1px solid ${C.orange}`,color:C.orange,borderRadius:4,padding:"2px 5px",fontWeight:700}}>🔥 TOP</span>}
                        </div>
                        <div style={{fontSize:10,color:C.textMuted}}>{spot.region}</div>
                        <div style={{display:"flex",gap:8,marginTop:2,flexWrap:"wrap"}}>
                          <span style={{fontSize:10,color:spot.liveFlow?"#4ade80":C.textMuted}}>💧 {spot.liveFlow?`${spot.liveFlow.cfs.toFixed(0)} cfs`:"est."}</span>
                          {spot.wx&&<span style={{fontSize:10,color:C.textSecondary}}>🌡 {spot.wx.temp}°F {spot.wx.desc}</span>}
                          {spot.dist!=null&&<span style={{fontSize:10,color:C.sand}}>📍 {spot.dist} mi</span>}
                        </div>
                      </div>
                      <span style={{color:C.textMuted,fontSize:12}}>{isOpen?"▲":"▼"}</span>
                    </div>
                    {isOpen&&(
                      <div style={{borderTop:`1px solid ${C.border}`,padding:13}}>
                        <p style={{fontSize:11,color:C.textSecondary,margin:"0 0 10px",lineHeight:1.6}}>{spot.description}</p>
                        <div style={{marginBottom:10}}>
                          <Bar label="Weather" value={spot.scores.weather} max={20} color="#38bdf8"/>
                          <Bar label="River Flow (USGS)" value={spot.scores.flow} max={20} color="#4ade80"/>
                          <Bar label="Pressure / Access" value={spot.scores.pressure} max={20} color={C.sand}/>
                          <Bar label="Feeding Window" value={spot.scores.feeding} max={16} color={C.orange}/>
                          <Bar label="Proximity" value={spot.scores.proximity} max={10} color={C.orangeLight}/>
                        </div>
                        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:10}}>
                          <div style={{background:C.navyMid,borderRadius:7,padding:9,border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:9,color:"#38bdf8",fontWeight:700,marginBottom:2}}>💧 FLOW</div>
                            {spot.liveFlow
                              ?<><div style={{fontSize:16,fontWeight:800,color:"#38bdf8"}}>{spot.liveFlow.cfs.toFixed(0)} <span style={{fontSize:10}}>cfs</span></div>
                                <div style={{fontSize:9,color:C.textMuted}}>Ideal: {spot.flowRange[0]}–{spot.flowRange[1]}</div>
                                <div style={{fontSize:10,fontWeight:600,color:spot.liveFlow.cfs>=spot.flowRange[0]&&spot.liveFlow.cfs<=spot.flowRange[1]?"#4ade80":C.sand}}>{spot.liveFlow.cfs>=spot.flowRange[0]&&spot.liveFlow.cfs<=spot.flowRange[1]?"✓ Ideal":"⚠ Out of range"}</div></>
                              :<div style={{fontSize:10,color:C.textMuted}}>Live data unavailable<br/><span style={{fontSize:9}}>Ideal: {spot.flowRange[0]}–{spot.flowRange[1]} cfs</span></div>}
                          </div>
                          <div style={{background:C.navyMid,borderRadius:7,padding:9,border:`1px solid ${C.border}`}}>
                            <div style={{fontSize:9,color:C.sand,fontWeight:700,marginBottom:2}}>🌤 WEATHER</div>
                            {spot.wx
                              ?<><div style={{fontSize:16,fontWeight:800,color:C.sand}}>{spot.wx.temp}°F</div>
                                <div style={{fontSize:10,color:C.textSecondary}}>{spot.wx.desc}</div>
                                <div style={{fontSize:9,color:C.textMuted}}>💨 {spot.wx.windSpeed} mph</div></>
                              :<div style={{fontSize:10,color:C.textMuted}}>Unavailable</div>}
                          </div>
                        </div>
                        <div style={{background:C.navyMid,borderRadius:7,padding:9,marginBottom:9,border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:9,color:C.orangeLight,fontWeight:700,marginBottom:5,textTransform:"uppercase"}}>{type==="fly"?"🪰 Recommended Flies:":"🎯 Recommended Lures:"}</div>
                          {spotFlies.map((f,i)=><FlyTag key={i} item={f}/>)}
                        </div>
                        <div style={{background:C.navyMid,borderRadius:7,padding:9,marginBottom:7,border:`1px solid ${C.border}`}}>
                          <div style={{fontSize:9,color:C.sand,fontWeight:700,marginBottom:2}}>🏪 {spot.shop.name}</div>
                          <div style={{fontSize:10,color:C.textSecondary}}>{spot.shop.address} · ★ {spot.shop.rating}</div>
                        </div>
                        <div style={{fontSize:10,color:C.textMuted,marginBottom:7}}>🐟 {spot.species.join(" · ")}</div>
                        <div style={{display:"flex",gap:7,flexWrap:"wrap",marginBottom:9}}>
                          <a href={`https://www.google.com/maps/dir/?api=1&destination=${spot.accessLat},${spot.accessLng}&travelmode=driving`} target="_blank" rel="noopener noreferrer" style={{fontSize:11,background:`${C.orange}22`,border:`1px solid ${C.orange}66`,color:C.orange,padding:"6px 12px",borderRadius:6,textDecoration:"none",fontWeight:700}}>🗺 Directions</a>
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.shop.name+" "+spot.shop.address)}`} target="_blank" rel="noopener noreferrer" style={{fontSize:11,background:`${C.sand}22`,border:`1px solid ${C.sand}66`,color:C.sand,padding:"6px 12px",borderRadius:6,textDecoration:"none",fontWeight:700}}>🏪 Shop Map</a>
                        </div>
                        <div style={{fontSize:11,color:C.textSecondary,fontStyle:"italic",borderLeft:`3px solid ${C.border}`,paddingLeft:9,marginBottom:7}}>{spot.tips}</div>
                        <div style={{fontSize:11,color:C.sand,fontWeight:600}}>📋 {spot.regulations}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        }
        <div style={{textAlign:"center",fontSize:10,color:C.textMuted,padding:"12px 0 6px",borderTop:`1px solid ${C.border}`,marginTop:14}}>
          USGS Water Services · Open-Meteo · State Fish &amp; Wildlife Agencies
        </div>
      </div>
    </div>
  );
}

// ─── WEBSITE DATA ─────────────────────────────────────────────────────────────
const NAV_LINKS = ["Home", "App", "Whistler", "Reports", "Pricing", "About"];

const SAMPLE_REPORTS = [
  { id: 1, name: "Mike T.", location: "Lake Tahoe, CA", species: "Rainbow Trout", weight: "4.2 lbs", conditions: "Early morning, light wind", date: "Feb 24, 2026", notes: "Hit them on a small spinner near the inlet." },
  { id: 2, name: "Sara J.", location: "Gulf of Mexico, FL", species: "Red Snapper", weight: "8.5 lbs", conditions: "Offshore, 2ft swells", date: "Feb 22, 2026", notes: "Live bait at 80ft depth did the trick." },
  { id: 3, name: "Dan R.", location: "Columbia River, OR", species: "Chinook Salmon", weight: "22 lbs", conditions: "Overcast, incoming tide", date: "Feb 20, 2026", notes: "Back trolling with a plug early morning." },
  { id: 4, name: "Jen M.", location: "Florida Keys, FL", species: "Permit", weight: "15 lbs", conditions: "Sunny, clear flats", date: "Feb 18, 2026", notes: "Crab pattern fly on an incoming tide." },
];

const FEATURES = [
  { icon: "🧠", title: "Instinct Meets Intelligence", desc: "AI-powered analysis that thinks like an angler — reading conditions, tides, and patterns so you don't have to." },
  { icon: "🌊", title: "Tides & Weather", desc: "Real-time tide charts, wind, swell, and barometric pressure all in one clean dashboard." },
  { icon: "📍", title: "Trip Reports", desc: "See what anglers are catching near you and share your own success stories with the community." },
  { icon: "🎯", title: "Best Time Predictions", desc: "Know the optimal window before you even leave the house. Smarter days outside start here." },
];

const DUMMY_USERS = [{ email: "demo@test.com", password: "password", name: "Demo User" }];

const ReportCard = ({ r, s }) => (
  <div style={s.reportCard}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
      <div>
        <div style={{ fontWeight: "bold", color: C.sand, fontSize: 16 }}>{r.name}</div>
        <div style={{ color: C.slateLight, fontSize: 13, marginTop: 2 }}>📍 {r.location}</div>
      </div>
      <div style={{ textAlign: "right" }}>
        <div style={s.badge}>{r.species}{r.weight ? ` · ${r.weight}` : ""}</div>
        <div style={{ color: C.slate, fontSize: 12, marginTop: 4 }}>{r.date}</div>
      </div>
    </div>
    {r.conditions && <div style={{ color: C.slateLight, fontSize: 13, marginTop: 12 }}>🌤 Conditions: {r.conditions}</div>}
    {r.notes && <div style={{ color: C.sandLight, fontSize: 14, marginTop: 10, fontStyle: "italic", borderLeft: `3px solid ${C.orange}`, paddingLeft: 12 }}>{r.notes}</div>}
  </div>
);

  const ReportsPage = ({ reports, reportForm, setReportForm, reportSubmitted, user, submitReport, s, nav, setAuthMode }) => (
    <div style={s.section}>
      <div style={s.sectionTitle}>Fishing Reports</div>
      <div style={s.divider} />
      <div style={{ ...s.sectionSub, marginBottom: 32 }}>Real catches from real anglers.</div>
      {user ? (
        <div style={{ ...s.card, marginBottom: 36 }}>
          <div style={{ fontSize: 17, color: C.sand, fontWeight: "bold", marginBottom: 20 }}>📝 Post a Report</div>
          {reportSubmitted && <div style={s.success}>✓ Report posted! Thanks, {user.user_metadata?.full_name || user.email}.</div>}
          <div style={s.formGrid}>
            <div style={s.formGroup}><label style={s.label}>Location *</label><input style={s.input} value={reportForm.location} onChange={e => setReportForm({ ...reportForm, location: e.target.value })} placeholder="e.g. Lake Tahoe, CA" /></div>
            <div style={s.formGroup}><label style={s.label}>Species *</label><input style={s.input} value={reportForm.species} onChange={e => setReportForm({ ...reportForm, species: e.target.value })} placeholder="e.g. Largemouth Bass" /></div>
            <div style={s.formGroup}><label style={s.label}>Weight</label><input style={s.input} value={reportForm.weight} onChange={e => setReportForm({ ...reportForm, weight: e.target.value })} placeholder="e.g. 6.5 lbs" /></div>
            <div style={s.formGroup}><label style={s.label}>Conditions</label><input style={s.input} value={reportForm.conditions} onChange={e => setReportForm({ ...reportForm, conditions: e.target.value })} placeholder="e.g. Cloudy, light chop" /></div>
          </div>
          <div style={s.formGroup}><label style={s.label}>Notes</label><textarea style={{ ...s.input, minHeight: 80, resize: "vertical" }} value={reportForm.notes} onChange={e => setReportForm({ ...reportForm, notes: e.target.value })} placeholder="What worked? Lure, technique, time of day..." /></div>
          <button style={s.btnPrimary} onClick={submitReport}>Post Report</button>
        </div>
      ) : (
        <div style={{ ...s.card, marginBottom: 36, textAlign: "center", padding: 36 }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🎣</div>
          <div style={{ fontSize: 18, color: C.sand, fontWeight: "bold", marginBottom: 8 }}>Share Your Catch</div>
          <div style={{ color: C.slateLight, fontSize: 14, marginBottom: 24, fontStyle: "italic" }}>Create a free account to post fishing reports.</div>
          <button style={s.btnPrimary} onClick={() => { setAuthMode("signup"); nav("Account"); }}>Create Free Account</button>
          <button style={s.btnSecondary} onClick={() => { setAuthMode("login"); nav("Account"); }}>Sign In</button>
        </div>
      )}
      {reports.map(r => <ReportCard key={r.id} r={r} s={s} />)}
    </div>
  );

    const PAGE_TO_URL = {
    Home:         "/",
    App:          "/app",
    Whistler:     "/fishwhistler",
    Reports:      "/reports",
    Pricing:      "/pricing",
    About:        "/about",
    Account:      "/account",
    Confirmation: "/success",
  };

  const URL_TO_PAGE = Object.fromEntries(
    Object.entries(PAGE_TO_URL).map(([page, url]) => [url.replace("/", ""), page])
  );

// ─── MAIN WEBSITE ─────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("Home");
  const [reports, setReports] = useState([]);
  const [user, setUser] = useState(null);
  const [authMode, setAuthMode] = useState("login");
  const [authForm, setAuthForm] = useState({ name: "", email: "", password: "" });
  const [authError, setAuthError] = useState("");
  const [users, setUsers] = useState(DUMMY_USERS);
  const [reportForm, setReportForm] = useState({ location: "", species: "", weight: "", conditions: "", notes: "" });
  const [reportSubmitted, setReportSubmitted] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);

  useEffect(() => {                                                   
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);                  
    return () => window.removeEventListener("resize", handleResize);
  }, []); 

useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

useEffect(() => {
  supabase.auth.getSession().then(({ data }) => {
    if (data.session?.user) setUser(data.session.user);
  });

  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    setUser(session?.user ?? null);
  });

  return () => subscription.unsubscribe();
}, []);

useEffect(() => {
  const handler = (e) => {
    e.preventDefault();
    setInstallPrompt(e);
    setShowInstallBtn(true);
  };
  window.addEventListener("beforeinstallprompt", handler);
  return () => window.removeEventListener("beforeinstallprompt", handler);
}, []);

const handleInstall = async () => {
  if (installPrompt) {
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setShowInstallBtn(false);
  } else {
    alert("To add to your home screen: tap the Share button in Safari, then tap 'Add to Home Screen'.");
  }
};

// Handle direct URL navigation
useEffect(() => {
  const applyPath = () => {
    const path = window.location.pathname.replace("/", "");
    const matched = URL_TO_PAGE[path];
    if (matched) setPage(matched);
  };
  applyPath();
  window.addEventListener("popstate", applyPath);
  return () => window.removeEventListener("popstate", applyPath);
}, []);

useEffect(() => {
  const loadReports = async () => {
    const { data, error } = await supabase
      .from("reports")
      .select("*")
      .order("created_at", { ascending: false });

    if (!error) setReports(data);
  };
  loadReports();
}, []);

  const nav = (p) => {
  setPage(p);
  window.history.pushState({}, "", PAGE_TO_URL[p] ?? "/");
  window.scrollTo(0, 0);
  window.gtag?.("event", "page_view", { page_path: PAGE_TO_URL[p] ?? "/" });
};

  const handleAuth = async () => {
    setAuthError("");
    if (authMode === "login") {
      const { data, error } = await supabase.auth.signInWithPassword({ email: authForm.email, password: authForm.password });
      if (error) { setAuthError(error.message); return; }
      setUser(data.user);
      setAuthForm({ name: "", email: "", password: "" });
      nav("Home");
    } else {
      const { data, error } = await supabase.auth.signUp({
        email: authForm.email,
        password: authForm.password,
        options: { data: { full_name: authForm.name } }
      });
      if (error) { setAuthError(error.message); return; }
      setUser(data.user);
      setAuthForm({ name: "", email: "", password: "" });
      nav("Home");
    }
  };

  const submitReport = async () => {
    if (!reportForm.location || !reportForm.species) return;

    const newReport = {
      ...reportForm,
      name: user.user_metadata?.full_name || user.email,
      user_id: user.id,
      date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }),
    };

    const { data, error } = await supabase
      .from("reports")
      .insert([newReport])
      .select()
      .single();

    if (error) {
      console.error("Failed to save report:", error.message);
      return;
    }

    setReports([data, ...reports]);
    setReportForm({ location: "", species: "", weight: "", conditions: "", notes: "" });
    setReportSubmitted(true);
    setTimeout(() => setReportSubmitted(false), 3000);
  };

  const s = {
    app: { fontFamily: "'Georgia', serif", background: C.navy, color: C.white, minHeight: "100vh" },
    nav: { background: "rgba(10,22,36,0.98)", borderBottom: `1px solid ${C.border}`, padding: "0 32px", display: "flex", alignItems: "center", justifyContent: "space-between", height: 64, position: "sticky", top: 0, zIndex: 100 },
    logo: { fontSize: 22, fontWeight: "bold", color: C.orange, letterSpacing: 2, cursor: "pointer", fontFamily: "Georgia, serif" },
    logoSub: { fontSize: 10, color: C.slateLight, letterSpacing: 3, display: "block", marginTop: -4 },
    navLinks: { display: "flex", gap: 28, alignItems: "center" },
    navLink: (active) => ({ background: "none", border: "none", color: active ? C.orange : C.sand, fontSize: 14, cursor: "pointer", letterSpacing: 1, fontFamily: "Georgia, serif", borderBottom: active ? `2px solid ${C.orange}` : "2px solid transparent", paddingBottom: 2 }),
    hero: { background: `linear-gradient(160deg, #080f18 0%, ${C.navyMid} 60%, #0d1e30 100%)`, padding: "20px 24px 40px", textAlign: "center", borderBottom: `1px solid ${C.border}` },
    heroTag: { display: "inline-block", background: "rgba(196,94,42,0.15)", color: C.orange, fontSize: 11, letterSpacing: 4, padding: "6px 20px", borderRadius: 2, marginBottom: 28, border: `1px solid rgba(196,94,42,0.3)`, textTransform: "uppercase" },
    h1: { fontSize: "clamp(38px, 6vw, 74px)", fontWeight: "bold", color: C.white, lineHeight: 1.1, marginBottom: 8 },
    h1accent: { color: C.orange },
    tagline: { fontSize: 18, color: C.sand, marginBottom: 12, fontStyle: "italic" },
    subTagline: { fontSize: 14, color: C.slateLight, marginBottom: 44, letterSpacing: 2, textTransform: "uppercase" },
    btnPrimary: { background: C.orange, color: "#fff", border: "none", padding: "14px 40px", fontSize: 15, fontWeight: "bold", cursor: "pointer", letterSpacing: 2, borderRadius: 2, textTransform: "uppercase" },
    btnSecondary: { background: "transparent", color: C.sand, border: `1px solid ${C.sand}`, padding: "14px 40px", fontSize: 15, cursor: "pointer", letterSpacing: 2, borderRadius: 2, marginLeft: 16 },
    section: { maxWidth: 1080, margin: "0 auto", padding: "80px 24px" },
    sectionTitle: { fontSize: "clamp(24px, 4vw, 38px)", color: C.white, marginBottom: 8, fontWeight: "bold" },
    divider: { width: 50, height: 3, background: C.orange, margin: "14px 0 12px" },
    sectionSub: { color: C.slateLight, fontSize: 16, fontStyle: "italic", marginBottom: 48 },
    grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 20 },
    card: { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 4, padding: 28 },
    cardIcon: { fontSize: 32, marginBottom: 14 },
    cardTitle: { fontSize: 17, color: C.sand, marginBottom: 10, fontWeight: "bold" },
    cardDesc: { color: C.slateLight, lineHeight: 1.75, fontSize: 14 },
    reportCard: { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 4, padding: 24, marginBottom: 16 },
    badge: { background: "rgba(196,94,42,0.15)", color: C.orange, fontSize: 12, padding: "3px 12px", borderRadius: 2, border: `1px solid rgba(196,94,42,0.3)`, display: "inline-block" },
    input: { width: "100%", background: "#0d1b2a", border: `1px solid ${C.border}`, borderRadius: 2, padding: "10px 14px", color: C.white, fontSize: 15, fontFamily: "Georgia, serif", boxSizing: "border-box", outline: "none" },
    label: { fontSize: 12, color: C.slateLight, letterSpacing: 2, display: "block", marginBottom: 6, textTransform: "uppercase" },
    formGroup: { marginBottom: 16 },
    formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 20px" },
    authBox: { maxWidth: 420, margin: "80px auto", background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40 },
    authTitle: { fontSize: 24, color: C.white, marginBottom: 6, fontWeight: "bold" },
    authSub: { color: C.slateLight, fontSize: 14, marginBottom: 28, fontStyle: "italic" },
    authToggle: { background: "none", border: "none", color: C.orange, cursor: "pointer", fontSize: 14, fontFamily: "Georgia, serif", textDecoration: "underline" },
    error: { color: "#e06060", fontSize: 13, marginBottom: 16 },
    success: { background: "rgba(60,180,80,0.1)", border: "1px solid rgba(60,180,80,0.3)", color: "#6dcc80", padding: "12px 16px", borderRadius: 2, marginBottom: 20, fontSize: 14 },
    footer: { background: "#080f18", borderTop: `1px solid ${C.border}`, padding: "48px 24px", textAlign: "center" },
    footerLogo: { fontSize: 20, color: C.orange, fontWeight: "bold", letterSpacing: 2, marginBottom: 8 },
    footerText: { color: C.slate, fontSize: 13, lineHeight: 1.8 },
    stripeBanner: { background: C.orange, padding: "18px 24px", textAlign: "center" },
  };

  const HomePage = () => (
    <div style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.6s" }}>
      <div style={s.hero}>
        <img src={logoPrimaryClear} alt="One Last Cast" style={{ width: 560, maxWidth: "85vw", objectFit: "contain", marginBottom: 0 }} />
        <p style={{ color: C.slateLight, fontSize: 12, letterSpacing: 3, textTransform: "uppercase", margin: "4px 0" }}>an OLC Outdoors production</p>
        <a href="https://www.olcoutdoors.com" target="_blank" rel="noopener noreferrer">
          <img src={logoOLC} alt="OLC Outdoors" style={{ width: 200, maxWidth: "50vw", objectFit: "contain", marginBottom: 0, cursor: "pointer" }} />
        </a>
        <h1 style={{ ...s.h1, marginTop: 16 }}>One Last <span style={s.h1accent}>Cast</span></h1>
        <p style={s.tagline}>"Instinct Meets Intelligence"</p>
        <p style={s.subTagline}>Plan Less. Experience More.</p>
        <button style={{ ...s.btnSecondary, borderColor: C.sand, color: C.sand }} onClick={() => window.location.href = "/app"}>
          Launch One Last Cast App
        </button>

        <button
          style={{ ...s.btnPrimary, background: C.orange, borderColor: C.orange, color: "#fff", marginLeft: 12, marginTop: 12, display: "inline-flex", alignItems: "center", gap: 8 }}
          onClick={() => nav("Whistler")}>
          Talk to the Fish Whistler
          <span style={{ fontSize: 9, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 3, padding: "2px 6px", letterSpacing: 1.5, fontWeight: 700 }}>PREMIUM</span>
        </button>

        {(showInstallBtn || /iphone|ipad|ipod/i.test(navigator.userAgent)) && (
          <div style={{ marginTop: 16 }}>
            <button
              style={{ ...s.btnSecondary, borderColor: C.slate, color: C.slateLight, fontSize: 12 }}
              onClick={handleInstall}>
              Add One Last Cast to Home Screen
            </button>
          </div>
        )}
      </div>
      {/* ── Tool Explainer ── */}
      <div style={{ background: "#080f18", borderBottom: `1px solid ${C.border}`, padding: "72px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 40 }}>

          {/* One Last Cast App */}
          <div style={{ textAlign: "center" }}>
            <img src={logoPrimaryClear} alt="One Last Cast" style={{ width: 240, maxWidth: "70vw", objectFit: "contain", marginBottom: 20 }} />
            <div style={{ fontSize: 11, color: C.slateLight, letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>River Conditions. Hatches. Scores.</div>
            <div style={{ width: 40, height: 2, background: C.sand, margin: "0 auto 20px" }} />
            <p style={{ color: C.slateLight, fontSize: 15, lineHeight: 1.85 }}>
              {(() => {
                const m = new Date().getMonth() + 1;
                const hatch = HATCH_BASE[m]?.fly;
                const top = hatch ? hatch.slice(0, 2).map(f => f.n).join(" and ") : "the hatches";
                const intros = {
                  1:  "It's January. Cold, quiet, and the fish are still eating.",
                  2:  "February — the water is low and clear and the midges are everywhere.",
                  3:  "It's mid-March. The Hendricksons are about to pop and the BWOs are already showing on warm afternoons.",
                  4:  "April is here. Hendricksons, March Browns, caddis — it's all happening at once.",
                  5:  "May. The best month. Sulphurs, Green Drakes, and caddis all showing up to the party.",
                  6:  "June — Sulphur spinner falls at dusk, Yellow Sallies in the riffles, and big fish looking up.",
                  7:  "July heat means Tricos in the morning and hoppers by afternoon.",
                  8:  "August. Trico spinners at first light, beetles and ants by midday.",
                  9:  "September — BWOs are back, streamers are starting to work, and the crowds are gone.",
                  10: "October. Streamers, egg patterns, and big brown trout on the move.",
                  11: "November — the crowds are long gone and the midges and BWOs are all yours.",
                  12: "December. Cold hands, tiny flies, and more fish than you'd expect.",
                };
                return `${intros[m]} Right now ${top} are the play, and there's a river near you running at fishable flows. Live USGS conditions, hatch reports tuned to the month, fly and lure suggestions, and a straight score on whether it's worth the drive — all in one place. Stop guessing. Go fish.`;
              })()}
            </p>
            <button style={{ ...s.btnSecondary, borderColor: C.sand, color: C.sand, marginTop: 24 }} onClick={() => window.location.href = "/app"}>
              Launch One Last Cast
            </button>
          </div>

          {/* Fish Whistler */}
          <div style={{ textAlign: "center" }}>
            <img src="/Fish_Whistler_logo1_transparent.png" alt="The Fish Whistler" style={{ width: 260, maxWidth: "60vw", objectFit: "contain", marginBottom: 12 }} />
            <div style={{ fontSize: 11, color: C.slateLight, letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>Legend. Oracle. Fish Whisperer.</div>
            <div style={{ width: 40, height: 2, background: C.orange, margin: "0 auto 20px" }} />
            <p style={{ color: C.slateLight, fontSize: 15, lineHeight: 1.85 }}>
              Somewhere deep in the Appalachians, an old timer has forgotten more about fishing than most people will ever know. He doesn't use apps. He reads the water. And legend has it, he doesn't even need to cast — he just whistles and they come. Ask him anything — flies, tactics, gear, that hatch nobody can identify — he's seen it all and he will tell you exactly what to do about it. The fish come when he calls. Will they come for you?
            </p>
            <button
              style={{ ...s.btnPrimary, marginTop: 24, display: "inline-flex", alignItems: "center", gap: 8 }}
              onClick={() => nav("Whistler")}>
              Talk to the Fish Whistler
              <span style={{ fontSize: 9, background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 3, padding: "2px 6px", letterSpacing: 1.5, fontWeight: 700 }}>PREMIUM</span>
            </button>
          </div>

        </div>
      </div>
      <div style={{ background: C.navyMid, borderBottom: `1px solid ${C.border}` }}>
        <div style={s.section}>
          <div style={s.sectionTitle}>Smarter Days Outside</div>
          <div style={s.divider} />
          <div style={s.sectionSub}>Everything you need. Nothing you don't.</div>
          <div style={s.grid}>
            {FEATURES.map((f, i) => (
              <div key={i} style={s.card}>
                <div style={s.cardIcon}>{f.icon}</div>
                <div style={s.cardTitle}>{f.title}</div>
                <div style={s.cardDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div style={s.stripeBanner}>
        <div style={{ fontSize: 18, color: "#fff", fontWeight: "bold" }}>Ready to fish smarter?</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 4, marginBottom: 16, fontStyle: "italic" }}>Join anglers already using One Last Cast.</div>
        <button style={{ ...s.btnPrimary, background: "#fff", color: C.orange }} onClick={() => window.location.href = "/app"}>Try It Free</button>
      </div>
      <div style={s.section}>
        <div style={s.sectionTitle}>Latest Reports</div>
        <div style={s.divider} />
        <div style={{ ...s.sectionSub, marginBottom: 32 }}>What anglers are catching right now.</div>
        {reports.slice(0, 2).map(r => <ReportCard key={r.id} r={r} s={s} />)}
        <div style={{ textAlign: "center", marginTop: 32 }}>
          <button style={s.btnPrimary} onClick={() => nav("Reports")}>See All Reports</button>
        </div>
      </div>
    </div>
  );

  const AboutPage = () => (
    <div>
      <div style={{ background: `linear-gradient(160deg, #080f18, ${C.navyMid})`, padding: "80px 24px 60px", textAlign: "center", borderBottom: `1px solid ${C.border}` }}>
        <div style={s.heroTag}>Our Story</div>
        <h1 style={{ ...s.h1, fontSize: "clamp(30px, 5vw, 54px)" }}>Built by Anglers,<br />for Anglers</h1>
        <p style={s.tagline}>"Plan Less. Experience More."</p>
      </div>
      <div style={s.section}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <div style={s.sectionTitle}>What is One Last Cast?</div>
          <div style={s.divider} />
          <p style={{ color: C.slateLight, lineHeight: 1.9, fontSize: 16, marginBottom: 24 }}>One Last Cast was born from a simple frustration — too much time spent checking five different apps, reading tide charts, cross-referencing weather, and still showing up at the wrong time. We believed there had to be a better way.</p>
          <p style={{ color: C.slateLight, lineHeight: 1.9, fontSize: 16, marginBottom: 24 }}>So we built it. One Last Cast pulls together tides, weather, moon phases, barometric pressure, and local fishing intel into a single, intelligent platform. Whether you're chasing stripers on the coast, targeting walleye on a Midwest lake, or throwing dry flies in a mountain stream — we've got you covered.</p>
          <p style={{ color: C.slateLight, lineHeight: 1.9, fontSize: 16, marginBottom: 48 }}>This is about spending less time planning and more time doing what you love. <em style={{ color: C.sand }}>Instinct meets intelligence.</em></p>
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 40 }}>
            <div style={s.sectionTitle}>Our Mission</div>
            <div style={s.divider} />
            <p style={{ color: C.slateLight, lineHeight: 1.9, fontSize: 16 }}>To give every angler — beginner or seasoned — the confidence to get on the water at the right time, in the right spot, with the right approach. Smarter days outside for everyone.</p>
          </div>
        </div>
      </div>
    </div>
  );

  const AccountPage = () => (
  user ? (
    <div style={{ ...s.authBox, textAlign: "center" }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>👤</div>
      <div style={s.authTitle}>Welcome, {user.user_metadata?.full_name || user.email}</div>
      <div style={{ color: C.slateLight, fontSize: 14, marginBottom: 28 }}>{user.email}</div>
      <button style={s.btnPrimary} onClick={async () => { await supabase.auth.signOut(); setUser(null); nav("Home"); }}>Sign Out</button>
    </div>
  ) : (
    <Auth onLogin={(u) => { setUser(u); nav("Home"); }} />
  )
);

const pages = { 
  Home: <HomePage />, 
  App: <FishingApp />, 
  Whistler: <FishWhistler nav={nav} />,
  Reports: <ReportsPage
    reports={reports}
    reportForm={reportForm}
    setReportForm={setReportForm}
    reportSubmitted={reportSubmitted}
    user={user}
    submitReport={submitReport}
    s={s}
    nav={nav}
    setAuthMode={setAuthMode}
  />, 
  About: <AboutPage />, 
  Account: <AccountPage /> ,
  Pricing: <PricingPage user={user} nav={nav} s={s} />,
  Confirmation: <ConfirmationPage nav={nav} s={s} />,
};


return (
    <div style={s.app}>
      <nav style={{ ...s.nav, position: "relative" }}>
        {logoHorizontal
          ? <img src={logoHorizontal} alt="One Last Cast" style={{ height: 46, cursor: "pointer" }} onClick={() => nav("Home")} />
          : <div style={s.logo} onClick={() => nav("Home")}>ONE LAST CAST<span style={s.logoSub}>FISHING INTELLIGENCE</span></div>
        }
        <div style={{ ...s.navLinks, display: isMobile ? "none" : "flex" }}>
          {NAV_LINKS.map(l => (
            <button key={l} style={s.navLink(page === l)} onClick={() => {
              if (l === "App") window.location.href = "/app";
              else if (l === "Whistler") nav("Whistler");
              else nav(l);
            }}>{l}</button>
          ))}
          {/* Support Us — now plain/normal */}
          <a href="https://ko-fi.com/onelastcast" target="_blank" rel="noopener noreferrer" style={{ display:"flex", alignItems:"center", padding:"5px 12px", color:C.sand, fontSize:14, letterSpacing:1, fontFamily:"Georgia, serif", textDecoration:"none", cursor:"pointer" }}>Support Us</a>
          {/* Sign In — now gets the orange bordered button style */}
          <button style={{ ...s.navLink(page === "Account"), display:"flex", alignItems:"center", padding:"5px 12px", borderRadius:2, border:`1px solid ${C.orange}66`, background:`${C.orange}15`, color:C.orange, fontSize:14, fontFamily:"Georgia, serif", letterSpacing:1, cursor:"pointer" }} onClick={() => nav("Account")}>{user ? `👤 ${(user.user_metadata?.full_name || user.email).split(" ")[0]}` : "Sign In/Sign Up"}</button>
        </div>
        <button onClick={() => setMenuOpen(p => !p)} style={{ display: isMobile ? "block" : "none", background: "none", border: "none", color: C.sand, fontSize: 26, cursor: "pointer" }}>
          {menuOpen ? "✕" : "☰"}
        </button>
        {menuOpen && (
          <div style={{ position: "absolute", top: 64, left: 0, right: 0, background: C.navyMid, borderBottom: `1px solid ${C.border}`, display: "flex", flexDirection: "column", padding: "12px 24px", gap: 12, zIndex: 200 }}>
            {NAV_LINKS.map(l => (
              <button key={l} style={{ ...s.navLink(page === l), textAlign: "left", fontSize: 16 }} onClick={() => { l === "App" ? window.location.href = "https://onelastcast.org/app" : nav(l); setMenuOpen(false); }}>{l}</button>
            ))}
            {/* Mobile Support Us — plain */}
            <a href="https://ko-fi.com/onelastcast" target="_blank" rel="noopener noreferrer" style={{ color: C.sand, fontSize: 16, fontFamily: "Georgia, serif", textDecoration: "none" }}>🙏 Support Us</a>
            <a href="https://www.instagram.com/askthefishwhistler" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 8, color: C.sand, fontSize: 16, fontFamily: "Georgia, serif", textDecoration: "none" }}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg> @askthefishwhistler</a>
            {/* Mobile Sign In — orange bordered */}
            <button style={{ ...s.navLink(page === "Account"), textAlign: "left", fontSize: 16, border:`1px solid ${C.orange}66`, background:`${C.orange}15`, color:C.orange, fontWeight:"bold" }} onClick={() => { nav("Account"); setMenuOpen(false); }}>{user ? `👤 ${(user.user_metadata?.full_name || user.email).split(" ")[0]}` : "Sign In/Sign Up"}</button>
          </div>
        )}
      </nav>

      {pages[page]}

<footer style={s.footer}>
        <div style={s.footerLogo}>ONE LAST CAST</div>
        <div style={{ ...s.footerText, marginBottom: 24 }}>Instinct Meets Intelligence</div>

        {/* Social + Support Row */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 24, marginBottom: 24, flexWrap: "wrap" }}>
          <a href="https://www.instagram.com/askthefishwhistler" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.sand, fontSize: 13, fontFamily: "Georgia, serif", textDecoration: "none", opacity: 0.85 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none"/></svg> @askthefishwhistler
          </a>
          <span style={{ color: C.border }}>|</span>
          <a href="https://ko-fi.com/onelastcast" target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: C.sand, fontSize: 13, fontFamily: "Georgia, serif", textDecoration: "none", opacity: 0.85 }}>
            ☕ Support Us on Ko-fi
          </a>
        </div>
        
        {/* Contact Us */}
        <div style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, margin: "0 auto 24px", padding: "28px 16px", maxWidth: 480, width: "100%", textAlign: "center" }}>
          <a href="mailto:support@onelastcast.org" style={{ fontSize: 13, color: C.sand, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, textDecoration: "none", display: "block" }}>Contact Us</a>
          <div style={{ ...s.footerText, marginTop: 6, fontStyle: "italic" }}>Questions, feedback, or partnership inquiries — we'd love to hear from you.</div>
        </div>

        {/* Nav Links */}
        <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 24, flexWrap: "wrap" }}>
          {["Home", "App", "Reports", "Pricing", "About"].map(l => (
            <button key={l} style={{ background: "none", border: "none", color: C.slate, cursor: "pointer", fontSize: 13, fontFamily: "Georgia, serif" }} onClick={() => l === "App" ? window.location.href = "https://onelastcast.org/app" : nav(l)}>{l}</button>
          ))}
        </div>

        {/* Copyright */}
        <div style={s.footerText}>© 2026 One Last Cast · onelastcast.org · All rights reserved.</div>
      </footer>
    </div>
  );
}